import React, {useEffect, useState, useContext, useCallback, useMemo} from 'react';
import {Alert, Button, Card, Col, Image, ListGroup, Row} from "react-bootstrap";
import {
    fetchFullInfoAssignmentAndBook,
    editAssignment,
    fetchCurrentUserAssigns,
    fetchAssignmentsAdmin
} from "../../http/bookAPI";
import {
    FaClock,
    FaBook,
    FaHourglassHalf,
    FaArrowRight,
    FaCheckCircle,
    FaImage,
    FaCalendarAlt,
    FaTasks,
    FaMoneyBillWave,
    FaBookOpen, FaCoins, FaHourglassStart, FaListOl, FaFileWord, FaGlobe, FaBell, FaTrashAlt, FaStar, FaTags
} from "react-icons/fa";
import { observer } from "mobx-react-lite";
import { toast } from 'react-toastify';
import "react-toastify/dist/ReactToastify.css";
import { Context } from "../../index";
import CancelAssignment from "../modals/assign/CancelAssignment";
import RatingModal from "../modals/assign/RateAssignment";
import AmazonLinkParser from "./AmazonLinkParser";
import {
    check, getRedirectUrlApi,
    updateUserVirtBalanceConfirmAsinAndSearchTermApi
} from "../../http/userAPI";
import AmazonReviewParser from "./AmazonReviewParser";
import "animate.css"; // Подключаем анимации (если еще не подключено)
import CopyButton from "../modals/assign/CopyButton";
import Loader from "../Loader";
import AudibleReviewParser from "./AudibleReviewParser";
import ChangeAmzName from "../modals/assign/ChangeAmzName";
import AIhelper from "../aihelper/AiHelper";
import {AIHelperProvider} from "../aihelper/AIHelperContext";
import ReviewSubmiterUserSide from "./ReviewSubmiterUserSide";
import CacheUpdateGreenLine from "../../helpers/CacheUpdateGreenLine";
import {motion} from "framer-motion";
import ProofUploadDual from "./ProofUploadDual";
import {buildAssignmentTexts} from "./assignmentTexts";
import ProofUploadSingle from "./ProofUploadSingle";
import SimpleLogsViewerBA from "../logs/SimpleLogsViewerBA";
import TripleSpinner from "../../helpers/TripleSpinner";
import DownloadProgressModal from "../modals/DownloadProgressModal";
import {downloadWithProgress} from "../../helpers/downloadWithProgress";
import ProofImageCard from "../common/ProofImageCard";
import ImageLightbox from "../common/ImageLightbox";
import SetStarsRatingGuide from "./SetStarsRatingGuide";

const normalizeReviewerNameFingerprint = (value) =>
    String(value || "")
        .normalize("NFKD")
        .replace(/\p{M}/gu, "")
        .toLowerCase()
        .replace(/[^\p{L}\p{N}]+/gu, "");

const reviewerNamesMatch = (left, right) => {
    const leftName = normalizeReviewerNameFingerprint(left);
    const rightName = normalizeReviewerNameFingerprint(right);
    return Boolean(leftName && rightName && leftName === rightName);
};

const isStaffApprovedFinalAssignment = (assignment) =>
    assignment?.assignment_status === "SUCCESS" && assignment?.reviewer_reading_step === "STEP5";

const MINUTE_MS = 60 * 1000;
const READING_EXTRA_MINUTES = 1440;
const REVIEW_VISIBILITY_DEADLINE_MINUTES = 20160;
const REPORTED_EXP_GRACE_MINUTES = 10080;
const DUAL_PROOF_REQUIRED_ASS_TYPES = [10,11,14,15,18,19,20,21,22,23,24,27];
const SINGLE_PROOF_REQUIRED_ASS_TYPES = [28,29,30,31,32];
const PROOF_REQUIRED_ASS_TYPES = [
    ...DUAL_PROOF_REQUIRED_ASS_TYPES,
    ...SINGLE_PROOF_REQUIRED_ASS_TYPES,
];

const isProofRequiredAssignment = (assTypeId) => PROOF_REQUIRED_ASS_TYPES.includes(Number(assTypeId));

const formatCountdownParts = (targetMinutes, nowMs = Date.now()) => {
    const numericTarget = Number(targetMinutes);
    if (!Number.isFinite(numericTarget) || numericTarget <= 0) return null;

    const diffMs = Math.max(0, numericTarget * MINUTE_MS - nowMs);
    const totalSeconds = Math.floor(diffMs / 1000);
    const days = Math.floor(totalSeconds / 86400);
    const hours = Math.floor((totalSeconds % 86400) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    return {
        expired: diffMs <= 0,
        text: `${days}d ${String(hours).padStart(2, "0")}h ${String(minutes).padStart(2, "0")}m ${String(seconds).padStart(2, "0")}s`,
    };
};

const getReadingDeadlineMinutes = (assignment) => {
    const start = Number(assignment?.start_read_date);
    const turnaround = Number(assignment?.turnaround_time);
    if (!Number.isFinite(start) || !Number.isFinite(turnaround)) return null;
    return start + turnaround;
};

const getReviewVisibilityDeadlineMinutes = (assignment) => {
    const published = Number(assignment?.publish_rev_date);
    if (!Number.isFinite(published) || published <= 0) return null;
    return published + REVIEW_VISIBILITY_DEADLINE_MINUTES;
};

const CountdownValue = ({ label, targetMinutes, className = "text-danger" }) => {
    const [nowMs, setNowMs] = useState(Date.now());

    useEffect(() => {
        const timerId = window.setInterval(() => {
            setNowMs(Date.now());
        }, 1000);

        return () => window.clearInterval(timerId);
    }, []);

    const countdown = formatCountdownParts(targetMinutes, nowMs);
    if (!countdown) return null;

    return (
        <span className="ms-1">
            {label}
            <span className={`fw-semibold ${className}`}>
                {countdown.expired ? "expired" : countdown.text}
            </span>
        </span>
    );
};

const getCompletedSteps = (stepCode) => {
    switch(stepCode) {
        case "STEP1":
            return { accessBook: false, readBook: false, postReview: false, turnInAssignment: false };
        case "STEP2":
            return { accessBook: true, readBook: false, postReview: false, turnInAssignment: false };
        case "STEP3":
            return { accessBook: true, readBook: true, postReview: false, turnInAssignment: false };
        case "STEP4":
            return { accessBook: true, readBook: true, postReview: true, turnInAssignment: false };
        case "STEP5":
            return { accessBook: true, readBook: true, postReview: true, turnInAssignment: false };
        default:
            return { accessBook: false, readBook: false, postReview: false, turnInAssignment: false };
    }
};

const AssignFull = observer(({ setActiveComponent }) => {
    const [showCancelAssignment, setShowCancelAssignment] = useState(false);
    const { book, user } = useContext(Context);
    const [isSwitchChecked, setIsSwitchChecked] = useState(false);
    const [bookAssignments, setBookAssignments] = useState([]);
    const [loading, setLoading] = useState(false);
    const [assignment, setAssignment] = useState({});
    const [currentStep, setCurrentStep] = useState(book.selectedCurrentUserAssign?.reviewer_reading_step || "STEP1");
    const [completedSteps, setCompletedSteps] = useState(getCompletedSteps("STEP1"));
    const [showRatingModal, setShowRatingModal] = useState(false);
    const [amazonData, setAmazonData] = useState({ asin: "", keywords: "" });
    const [showExtractor, setShowExtractor] = useState(true);
    const [errorMessage, setErrorMessage] = useState("");
    const [serverMessage, setServerMessage] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [proofs, setProofs] = useState({ receipt: null, real: null });
    const [downloadModal, setDownloadModal] = useState({ show: false, progress: 0, status: "" });
    const [fullImage, setFullImage] = useState(null);

    const [reviewData, setReviewData] = useState(null);
    const [hoveredDel, setHoveredDel] = useState(false);
    const [error, setError] = useState("");
    const [showChangeAmzName, setShowChangeAmzName] = useState(false);

    const assId = book?.selectedCurrentUserAssign?.id;

    const openRedirectedLink = useCallback(async (link) => {
        if (!link) return;
        try {
            const { redirectUrl } = await getRedirectUrlApi(link);
            if (!redirectUrl) throw new Error("Redirect URL missing");
            window.open(redirectUrl, "_blank", "noopener,noreferrer");
        } catch (e) {
            console.error(e);
            toast.error("Could not open Amazon link safely. Please try again.", { position: "bottom-center", autoClose: 3000, hideProgressBar: false });
        }
    }, []);

    const handleFileUpload = async ({ receipt, real }) => {
        setIsUploading(true);
        try {
            setProofs((prev) => ({
                ...prev,
                receipt: receipt || prev.receipt,
                real: real || prev.real,
            }));
            console.log(receipt);
            console.log(proofs?.receipt);
        } catch (error) {
            console.error("Error uploading proof files:", error);
        } finally {
            setIsUploading(false);
        }
    };
    // ✅ Универсальный хэндлер: принимает { receipt } ИЛИ { proof }
    const handleFileUploadSingle = (payload = {}) => {
        setIsUploading(true);
        try {
            const node = payload.receipt || payload.proof;
            if (!node?.fileName) throw new Error("No file received from uploader");

            // аккуратный апдейт стейта (без гонок)
            setProofs(prev => ({
                ...prev,
                receipt: {
                    fileName: node.fileName,
                    hash: node.hash || null,
                },
            }));

            // логируем полученные данные (а не весь объект из стейта сразу после setState)
            console.log("Uploaded (single):", node.fileName, node.hash);
        } catch (error) {
            console.error("Error uploading proof files:", error);
        } finally {
            setIsUploading(false);
        }
    };


    useEffect(() => {
        const step = book.selectedCurrentUserAssign?.reviewer_reading_step || "STEP1";
        setCurrentStep(step);
        setCompletedSteps(getCompletedSteps(step));
    }, [book.selectedCurrentUserAssign?.reviewer_reading_step]);

    const assignmentTexts = buildAssignmentTexts({ book, user, openRedirectedLink });

    const getAssignmentData = (assTypeId, booksTypeId, step) => {
        const data = assignmentTexts?.[assTypeId]?.[booksTypeId]?.[step];
        if (!data) console.warn("⚠️ Missing assignment text for", { assTypeId, booksTypeId, step });
        return data || {
            icon: <FaHourglassHalf size={15} />,
            title: "No Step Information",
            description: "No Step Information",
            content: null,
        };
    };
// Использование:
    const assignmentData = getAssignmentData(
        Number(book.selectedCurrentUserAssign?.assTypeId),
        Number(book.selectedCurrentUserAssign?.booksTypeId),
        currentStep
    );

    const handleSwitchChange = (event) => {
        setIsSwitchChecked(event.target.checked);
    };


    const handleDownloadPhoto = async () => {
        setLoading(true); // Показываем лоадер

        const fileUrl = `${process.env.REACT_APP_API_URL}/photorev/${book.selectedCurrentUserAssign?.assignment_img1}`;

        try {
            const response = await fetch(fileUrl, { method: "GET" });

            if (!response.ok) {
                throw new Error(`Failed to download: ${response.status} ${response.statusText}`);
            }

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = book.selectedCurrentUserAssign?.assignment_img1 || "photo.jpg";
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error("Error downloading the photo:", error);
            alert("Failed to download the photo. Please try again.");
        } finally {
            setLoading(false); // Скрываем лоадер
        }
    };
    const handleDownload = async () => {
        const assignmentId = book.selectedCurrentUserAssign?.id;
        if (!assignmentId) return;
        setLoading(true);
        setDownloadModal({
            show: true,
            progress: 0,
            status: "Preparing secure manuscript download...",
        });

        try {
            await downloadWithProgress({
                path: `api/assignment/one/${assignmentId}/manuscript-download`,
                filename: `${book.selectedCurrentUserAssign?.book?.title || "virtalibry-manuscript"}.pdf`,
                onProgress: (progress) => setDownloadModal((prev) => ({ ...prev, progress })),
                onStatus: (status) => setDownloadModal((prev) => ({ ...prev, status })),
            });
            setTimeout(() => setDownloadModal((prev) => ({ ...prev, show: false })), 700);
        } catch (error) {
            console.error("Error downloading the file:", error);
            setDownloadModal((prev) => ({ ...prev, status: "Download failed. Please try again." }));
            toast.error("Failed to download the file. Please try again.", { position: "bottom-center", autoClose: 3000, hideProgressBar: false });
            setTimeout(() => setDownloadModal((prev) => ({ ...prev, show: false })), 1400);
        } finally {
            setLoading(false);
        }
    };

    const threeLineToast = (title, message, newBalance) => {
        toast.success(
            <div >
                <div style={{ fontSize: '14px', fontWeight: 'bold', lineHeight: '1.4' }}>{title}</div>
                <div style={{ fontSize: '12px', lineHeight: '1.2', marginTop: '4px' }}>{message}</div>
                <div style={{ fontSize: '12px', lineHeight: '1.2', marginTop: '4px' }}>{newBalance}</div>
            </div>,
            { position: "top-center", autoClose: 8000 }
        );
    };



    const handleExtract = async (data) => {
        setAmazonData(data);

        // Достаем ASIN и Keywords из assignobject
        const expectedAsin = book.selectedCurrentUserAssign?.book?.kindle_asin || "";
        const expectedKeywords = book.selectedCurrentUserAssign?.assignment_search_term || "";
        const assignmentId = book?.selectedCurrentUserAssign?.id;

        // Проверяем совпадение
        if (data.asin === expectedAsin && data.keywords === expectedKeywords) {
            try {
                const updateUserData = await updateUserVirtBalanceConfirmAsinAndSearchTermApi(assignmentId);

                threeLineToast(updateUserData.title, updateUserData.message, updateUserData.newBalance);

                // 📌 Обновляем баланс в MobX store
                const data = await check();
                user.setUser(data);

                setShowExtractor(false); // Скрываем компонент после успешной проверки
                setAmazonData(null);
            } catch (error) {
                const errorMessage = error.response?.data?.message || 'An error occurred.';
                setError(errorMessage);
                toast.error(`${errorMessage}`, { position: "bottom-center", autoClose: 3000, hideProgressBar: false });
            }
        } else {
            // Если данные не совпадают, выводим ошибку
            toast.error("Verification failed! ASIN or Keywords do not Match.", { position: "bottom-center", autoClose: 3000, hideProgressBar: false });
        }
    };

   useEffect(() => {

       if (book.currentuserassigns.length > 0) {
           fetchFullInfoAssignmentAndBook(book?.selectedCurrentUserAssign?.id)
               .then((data) => {
                   setAssignment(book?.selectedCurrentUserAssign);
                   const step = data.reviewer_reading_step || "STEP1";
                   setCurrentStep(step);
                  // setCompletedSteps(getCompletedSteps(step));

                   setErrorMessage(''); // Очищаем сообщение об ошибке
                   setAmazonData(null);

               })
               .catch((error) => {
                   toast.error("Error fetching assignment data", { position: "bottom-center", autoClose: 3000, hideProgressBar: false });

                   });
           }
       fetchCurrentUserAssigns(user?.user?.id).then(data => book.setCurrentUserAssigns(data));
    }, [currentStep, book?.selectedCurrentUserAssign?.id]);

    const date = new Date((book.selectedCurrentUserAssign?.start_read_date * 60 * 1000) + (book.selectedCurrentUserAssign?.turnaround_time * 60 * 1000));
    const startreaddate = new Date(book.selectedCurrentUserAssign?.start_read_date * 60 * 1000);

// Массив с сокращениями месяцев
    const months = [
        "January",
        "February",
        "March",
        "April",
        "May",
        "June",
        "July",
        "August",
        "September",
        "October",
        "November",
        "December"
    ];

    const startreadmonths = [
            "January",
            "February",
            "March",
            "April",
            "May",
            "June",
            "July",
            "August",
            "September",
            "October",
            "November",
            "December"
        ]
    ;

// Получаем месяц, день, год, часы и минуты

    const startreadmonth = startreadmonths[startreaddate.getMonth()];
    const startreadday = startreaddate.getDate();

    const daysToRead = Math.ceil(book.selectedCurrentUserAssign?.turnaround_time / 60 / 24 );
    // Текущее: start/turnaround в минутах
    const start = Number(book.selectedCurrentUserAssign?.start_read_date) || 0;
    const turnaround = Number(book.selectedCurrentUserAssign?.turnaround_time) || 0;
    const nowInMinutes = Date.now() / 1000 / 60;

// Новое: мин. до дедлайна (неотрицательно)
    const totalMinutesLeft = Math.max(0, Math.floor((start + turnaround) - nowInMinutes));

// Оставь округлённые дни для «плашек»
    let daysLeft = Math.ceil(totalMinutesLeft / (60 * 24));

// Обезопасим:
    if (!Number.isFinite(daysLeft) || daysLeft < 0) {
        daysLeft = 0;
    }
    const selectedAssignment = book.selectedCurrentUserAssign;
    const readingDeadlineMinutes = getReadingDeadlineMinutes(selectedAssignment);
    const autoTurnInDeadlineMinutes = readingDeadlineMinutes
        ? readingDeadlineMinutes + READING_EXTRA_MINUTES
        : null;
    const reviewVisibilityDeadlineMinutes = getReviewVisibilityDeadlineMinutes(selectedAssignment);
    const reportedExpGraceDeadlineMinutes = reviewVisibilityDeadlineMinutes
        ? reviewVisibilityDeadlineMinutes + REPORTED_EXP_GRACE_MINUTES
        : null;
   // Функция для обновления шага при нажатии "Done"
    const handleDoneStep = async (stepKey, stepCode) => {
        const assType = book?.selectedCurrentUserAssign?.assTypeId;

        // если нужны доказательства (чек + фото)
        if ([10, 11, 14, 15, 18, 19, 20, 21, 22, 23, 24, 27].includes(assType) && currentStep === 'STEP2') {
            try {
                const response = await fetch(`${process.env.REACT_APP_API_URL}api/assignment/proof_finalize`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        assignment_id: book?.selectedCurrentUserAssign?.id,
                        receipt: proofs?.receipt,
                        real: proofs?.real,
                    }),
                });

                const result = await response.json();
                if (!response.ok) throw new Error(result?.message || "Proof finalization failed");
                toast.success(result?.message || "Proofs uploaded successfully!", { position: "bottom-center", autoClose: 3000, hideProgressBar: false });
            } catch (err) {
                // ✅ Показываем точное сообщение ошибки
                toast.error(err?.message ? `Upload proof please: ${err.message}` : "Upload proof please.", { position: "bottom-center", autoClose: 3000, hideProgressBar: false });
                return; // Не продолжаем шаг, если ошибка
            }
        }
        if ([28, 29, 30, 31, 32].includes(assType) && currentStep === 'STEP2') {
            try {
                const response = await fetch(`${process.env.REACT_APP_API_URL}api/assignment/proof_finalize_single`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        assignment_id: book?.selectedCurrentUserAssign?.id,
                        receipt: proofs?.receipt,
                    }),
                });

                const result = await response.json();
                if (!response.ok) throw new Error(result?.message || "Proof finalization failed");
                toast.success(result?.message || "Proofs uploaded successfully!", { position: "bottom-center", autoClose: 3000, hideProgressBar: false });
            } catch (err) {
                // ✅ Показываем точное сообщение ошибки
                toast.error(err?.message ? `Upload proof please: ${err.message}` : "Upload proof please.", { position: "bottom-center", autoClose: 3000, hideProgressBar: false });
                return; // Не продолжаем шаг, если ошибка
            }
        }
        try {
            await editAssignment({
                id: book.selectedCurrentUserAssign?.id,
                reviewer_reading_step: stepCode,
            });
            const data = await fetchFullInfoAssignmentAndBook(book.selectedCurrentUserAssign?.id);
            book?.setSelectedCurrentUserAssign(data);
            setCurrentStep(stepCode);
         //   setCompletedSteps(getCompletedSteps(stepCode));
           // toast.success("Step updated successfully", { position: "bottom-center", autoClose: 3000 });
        } catch (error) {
            console.error("Error updating step", error);
            toast.error("Error updating step", { position: "bottom-center", autoClose: 3000, hideProgressBar: false });
        }
    };

    const refetchThisAssignment = useCallback(async () => {
        if (!assId) return;
        setLoading(true);
        try {
            const data = await fetchFullInfoAssignmentAndBook(assId);
            book?.setSelectedCurrentUserAssign(data);
        } finally {
            setLoading(false);
        }
    }, [assId]);

   const handleExtractAudibleReview = async (data) => {
        setReviewData(data);
        const expectedReviewAsin = assignmentData.book_asin || "";
        const expectedReviewerName = user?.user?.audible_name || "";
        const expectedRating = book.selectedCurrentUserAssign?.rating || 0;

        if (data.asin !== expectedReviewAsin ||
            data.listener_name !== expectedReviewerName ||
            Number(data.overall_rating) !== Number(expectedRating)) {
            setCurrentStep("STEP4");
            // ✅ Выводим тост об ошибке
            toast.error("Verification Failed! Check Rating, Reviewer Name or Asin.", { position: "bottom-center", autoClose: 3000, hideProgressBar: false });
            await editAssignment({
                id: book.selectedCurrentUserAssign?.id,
                review_link: data.review_link,
                checked_asin: data.asin,
                checked_review_name: data.listener_name,
                checked_review_rating: data.overall_rating,
                checked_story_rating: data.story_rating,
                checked_performance_rating: data.performance_rating,

            });
        } else {
            await editAssignment({
                id: book.selectedCurrentUserAssign?.id,
                reviewer_reading_step: 'STEP5',
                assignment_status: 'SUCCESS',
                review_link: data.review_link,
                end_rev_date: Math.floor(Date.now() / 1000 / 60),
                checked_asin: data.asin,
                checked_review_name: data.listener_name,
                checked_review_title: data.review_title,
                checked_review_text: data.review_text,
                checked_review_rating: data.overall_rating,
                checked_story_rating: data.story_rating,
                checked_performance_rating: data.performance_rating,
                checked_review_country: user?.user?.audible_country,
                checked_review_date: data.review_date,
                checked_proxy_country: data.proxy_country,

            });

            // ✅ Выводим тост об успешной проверке
            toast.success("Verification Review Link Successful!", { position: "bottom-center", autoClose: 3000, hideProgressBar: false });
        }



    };

    // Маппинг формата из booksId
    const EDITION_BY_BOOK_ID = {
        1: "Kindle Edition",
        2: "Paperback Edition",
        3: "Hardcover Edition",
        4: "Audiobook (Audible)",
        5: "Existing Review",
        6: "Existing Review",
        7: "Physical Product",
        8: "Physical Product",
        9: "Physical Product",
        10: "Physical Product",


    };

    const getExpectationByType = (assTypeId) => {
        // Покупка/ребейт и промо-покупки — ждём Verified Purchase
        if ([3, 5, 9, 18, 20].includes(Number(assTypeId))) {
            return "Verified Purchase Review";
        }
        if ([10, 14].includes(Number(assTypeId))) {
            return "Only BuyOut";
        }
        if ([11, 15].includes(Number(assTypeId))) {
            return "Search and BuyOut";
        }
        if ([19, 21].includes(Number(assTypeId))) {
            return "Search & Buy Verified Purchase Review";
        }
        if ([4, 6, 12, 16].includes(Number(assTypeId))) {
            return "Verified Purchase Review with Photos";
        }
        if ([13, 17].includes(Number(assTypeId))) {
            return "Verified Purchase Review with Video";
        }
        // Универсальные/прочие
        if ([1, 2, 7, 8].includes(Number(assTypeId))) {
            return "Non-Verified Review";
        }
        if ([22, 23].includes(Number(assTypeId))) {
            return "Product BuyOut Assignment";
        }
        if ([24, 27].includes(Number(assTypeId))) {
            return "Product Verified Purchase Review";
        }
        if ([25].includes(Number(assTypeId))) {
            return "Product Verified Purchase + 3 Photo";
        }
        if ([26].includes(Number(assTypeId))) {
            return "Product Verified Purchase + Video";
        }
        if ([28, 30].includes(Number(assTypeId))) {
            return "Tap Helpful";
        }
        if ([29, 31].includes(Number(assTypeId))) {
            return "Write Report";
        }
        if ([32].includes(Number(assTypeId))) {
            return "Set Stars Rating";
        }


        // Фоллбек
        return "Verified Purchase Review";
    };

    const phrase = (() => {
        const t = book.selectedCurrentUserAssign;
        const edition = EDITION_BY_BOOK_ID[Number(t?.booksTypeId)] || "Unknown Edition";
        const expectation = getExpectationByType(t?.assTypeId);
        return { edition, expectation };
    })();

    const getAssignmentBookAsin = () => {
        const isUsableAsin = (value) => {
            const text = String(value || "").trim();
            return Boolean(text) && text !== "undefined" && text !== "Not found";
        };

        const directAsin = assignmentData?.book_asin;
        if (isUsableAsin(directAsin)) return directAsin;

        const current = book.selectedCurrentUserAssign;
        const bookData = current?.book || {};
        switch (Number(current?.booksTypeId)) {
            case 1:
                return [bookData.kindle_asin, bookData.paper_back_asin, bookData.hard_cover_asin].find(isUsableAsin) || "";
            case 2:
                return [bookData.paper_back_asin, bookData.kindle_asin, bookData.hard_cover_asin].find(isUsableAsin) || "";
            case 3:
                return [bookData.hard_cover_asin, bookData.paper_back_asin, bookData.kindle_asin].find(isUsableAsin) || "";
            case 4:
                return [bookData.audio_audible_asin, bookData.audible_asin, bookData.kindle_asin].find(isUsableAsin) || "";
            default:
                return [bookData.kindle_asin, bookData.paper_back_asin, bookData.hard_cover_asin].find(isUsableAsin) || "";
        }
    };

    const openAssignmentBookPage = () => {
        const asin = getAssignmentBookAsin();
        if (!asin) {
            toast.error("Book ASIN is not available for this assignment.", { position: "bottom-center", autoClose: 3000, hideProgressBar: false });
            return;
        }
        openRedirectedLink(`${String(user?.user?.amz_country)}/dp/${asin}`);
    };

    // Внутри компонента
    const currentGenreName = React.useMemo(() => {
        const gid = book?.selectedCurrentUserAssign?.booksGenreId;
        if (!gid || !Array.isArray(book?.genres)) return "";

        const found = book.genres.find(g => Number(g.id) === Number(gid));
        return found?.genre || "";
    }, [book?.selectedCurrentUserAssign?.booksGenreId, book?.genres]);

    const a = book.selectedCurrentUserAssign;
    const selectedReviewerSnapshotName = (() => {
        const currentAssignment = book.selectedCurrentUserAssign || {};
        const isAudibleAssignment = Number(currentAssignment.assTypeId) === 8;
        const snapshotName = isAudibleAssignment
            ? currentAssignment.reviewer_audible_name_snapshot
            : currentAssignment.reviewer_amazon_name_snapshot;

        return snapshotName || currentAssignment.reviewer_name || "";
    })();

    // Хелпер для фото: если пришло только имя файла — собираем URL как {API}/photo_proofs/{file}
    const buildPhotoUrl = (val) => {
        if (!val) return null;
        if (/^https?:\/\//i.test(val)) return val;
        return `${process.env.REACT_APP_API_URL}photo_proofs/${val}`;
    };

    const photo1 = buildPhotoUrl(book?.selectedCurrentUserAssign?.checked_review_photo);
    const photo2 = buildPhotoUrl(book?.selectedCurrentUserAssign?.checked_review_photo2);
    const proofUrls = [photo1, photo2].filter(Boolean);
    const proofFallback = `${process.env.REACT_APP_API_URL}covers/empty-cover.jpg`;
    const coverSrc = book.selectedCurrentUserAssign?.book?.cover_file
        ? `${process.env.REACT_APP_API_URL}covers/${book.selectedCurrentUserAssign?.book?.cover_file}`
        : `${process.env.REACT_APP_API_URL}covers/empty-cover.jpg`;

    const fallbackCoverSrc = `${process.env.REACT_APP_API_URL}covers/empty-cover.jpg`;
    const CoverWithSpinner = ({
                                  src,
                                  fallbackSrc,
                                  alt = "",
                                  fetchPriority = "auto",
                              }) => {
        const [loaded, setLoaded] = useState(false);
        const [err, setErr] = useState(false);

        const finalSrc = err ? fallbackSrc : src;

        return (
            <div
                style={{
                    position: "relative",
                    width: "100%",
                    maxWidth: '150px',
                    display: "flex",
                    alignItems: "center",
                    objectFit: 'cover',
                    justifyContent: "center",
                    backgroundColor: "white",
                    borderRadius: 4,
                    overflow: "hidden",
                }}
            >
                {!loaded && (
                    <div
                        style={{
                            position: "absolute",
                            inset: 0,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                        }}
                        aria-hidden="true"
                    >
                        <TripleSpinner size={24} gapRatio={0.8} />
                    </div>
                )}

                <img
                    src={finalSrc}
                    alt={alt}
                    loading="lazy"
                    decoding="async"
                    fetchPriority={fetchPriority}
                    onLoad={() => setLoaded(true)}
                    onError={() => {
                        setErr(true);
                        setLoaded(true);
                    }}
                    style={{
                        maxWidth: 140,
                        maxHeight: 180,
                        width: "auto",
                        height: "auto",
                        opacity: loaded ? 1 : 0,
                        transition: "opacity 600ms ease-in",
                        backgroundColor: "white",
                        padding: 0,
                        borderRadius: 4,
                    }}
                />
            </div>
        );
    };

    return (
        <>
            <DownloadProgressModal
                show={downloadModal.show}
                progress={downloadModal.progress}
                status={downloadModal.status}
            />
            <ImageLightbox
                src={fullImage}
                alt="Full size proof image"
                onClose={() => setFullImage(null)}
            />
            {loading ? (
                <Loader />
            ) : (
        <>
            <CacheUpdateGreenLine />
         <Card className="p-3 w-100 mt-2">
            <div className="justify-content-between align-items-center align-items-md-center flex-wrap gap-2">
                <Card className="w-100 mt-0 mb-0 small">
                    <Card.Body>
                        <Row className="g-3">
                            {/* 📱 Картинка: на мобилке будет сверху */}
                            <Col xs={12} md={2} className="d-flex justify-content-center align-items-start">

                                <CoverWithSpinner
                                    src={coverSrc}
                                    fallbackSrc={fallbackCoverSrc}
                                    alt={book.selectedCurrentUserAssign?.book?.title}
                                />
                            </Col>

                            {/* 📱 Текст: на мобилке будет под картинкой */}
                            <Col xs={12} md={10} >
                                <p className="d-flex justify-content-between align-items-center fs-4 lh-sm fw-medium text-muted mb-0 ms-2">
                                    <span className="fs-5"> {book?.selectedCurrentUserAssign?.book?.title || 'No title available'}</span>
                                    <span className="fs-6 text-body-secondary">#{book?.selectedCurrentUserAssign?.book?.id}-{book?.selectedCurrentUserAssign?.id}</span>
                                </p>
                                <p className="fst-italic fs-6 lh-sm fw-normal mb-1 ms-2 text-muted text-start text-md-start">
                                    {book?.selectedCurrentUserAssign?.book?.subtitle || 'No subtitle available'}
                                </p>
                                <div className="d-flex justify-content-between align-items-center small mb-2 ms-2">
                                    {/* Автор и рейтинг слева */}
                                    <div className="text-start">
                                        by {book.selectedCurrentUserAssign?.book?.author || 'No author available'},&nbsp;
                                        {[...Array(5)]?.map((_, index) => (
                                            <i
                                                key={index}
                                                className={`bi bi-star${index < book.selectedCurrentUserAssign?.rating ? '-fill' : ''}`}
                                                style={{
                                                    color: index < book.selectedCurrentUserAssign?.rating ? 'gold' : 'lightgray',
                                                    fontSize: '12px',
                                                    marginRight: '4px'
                                                }}
                                            ></i>
                                        ))}
                                    </div>

                                    {/* Фраза справа */}
                                    <div className="text-end">
                                        <span className="text-danger fs-6 fw-bolder">{phrase.expectation}</span>{" "}
                                        for{" "}
                                        <span className="text-success fs-6 fw-semibold">{phrase.edition}{getAssignmentBookAsin()
                                            ? (<>
                                                    , Asin: {getAssignmentBookAsin()}
                                                </>

                                            )
                                            : null}</span>
                                    </div>
                                </div>


                                <Row className="g-2 small text-muted">
                                    <Col xs={12} md={5}>
                                        <ListGroup variant="success">
                                            <ListGroup.Item className="py-1 px-2 fst-italic mt-0 text-muted">

                                                <FaTasks className="me-2" />
                                                Type:
                                                <span className="fw-bolder">
                                                            {book?.selectedCurrentUserAssign?.assTypeId === 1 && " Universal Assignment"}
                                                    {book?.selectedCurrentUserAssign?.assTypeId === 2 && " Kindle Unlimited"}
                                                    {book?.selectedCurrentUserAssign?.assTypeId === 3 && " Verified Purchase"}
                                                    {book?.selectedCurrentUserAssign?.assTypeId === 4 && " Verified Purchase Img"}
                                                    {book?.selectedCurrentUserAssign?.assTypeId === 5 && " Ver/Pur Search&Buy"}
                                                    {book?.selectedCurrentUserAssign?.assTypeId === 6 && " Ver/Pur Search&Buy Img"}
                                                    {book?.selectedCurrentUserAssign?.assTypeId === 7 && " Universal Audio"}
                                                    {book?.selectedCurrentUserAssign?.assTypeId === 8 && " Audible Promo Code"}
                                                    {book?.selectedCurrentUserAssign?.assTypeId === 9 && " Verified Kindle Unlimited"}
                                                    {book?.selectedCurrentUserAssign?.assTypeId === 10 && " Paperback BuyOut"}
                                                    {book?.selectedCurrentUserAssign?.assTypeId === 11 && " Paperback Search & BuyOut "}
                                                    {book?.selectedCurrentUserAssign?.assTypeId === 12 && " PB Verified Purchase + Img"}
                                                    {book?.selectedCurrentUserAssign?.assTypeId === 13 && " PB Verified Purchase + Video"}
                                                    {book?.selectedCurrentUserAssign?.assTypeId === 14 && " Hardcover BuyOut"}
                                                    {book?.selectedCurrentUserAssign?.assTypeId === 15 && " Hardcover Search & BuyOut"}
                                                    {book?.selectedCurrentUserAssign?.assTypeId === 16 && " HC Verified Purchase + Img"}
                                                    {book?.selectedCurrentUserAssign?.assTypeId === 17 && " HC Verified Purchase + Video"}
                                                    {book?.selectedCurrentUserAssign?.assTypeId === 18 && " Paperback Verified Purchase"}
                                                    {book?.selectedCurrentUserAssign?.assTypeId === 19 && " Paperback VP Search & Buy"}
                                                    {book?.selectedCurrentUserAssign?.assTypeId === 20 && " Hardcover Verified Purchase"}
                                                    {book?.selectedCurrentUserAssign?.assTypeId === 21 && " Hardcover VP Search & Buy"}
                                                    {book?.selectedCurrentUserAssign?.assTypeId === 22 && " Product BuyOut"}
                                                    {book?.selectedCurrentUserAssign?.assTypeId === 23 && " Product Search & BuyOut"}
                                                    {book?.selectedCurrentUserAssign?.assTypeId === 24 && " Product Verified Purchase"}
                                                    {book?.selectedCurrentUserAssign?.assTypeId === 25 && " Product Verified + 3 Photo"}
                                                    {book?.selectedCurrentUserAssign?.assTypeId === 26 && " Product Verified + Video"}
                                                    {book?.selectedCurrentUserAssign?.assTypeId === 27 && " Product VP Search & Buy"}
                                                    {book?.selectedCurrentUserAssign?.assTypeId === 28 && " Make Review Helpful (Internal)"}
                                                    {book?.selectedCurrentUserAssign?.assTypeId === 29 && " Write Report (Internal)"}
                                                    {book?.selectedCurrentUserAssign?.assTypeId === 30 && " Make Review Helpful (External)"}
                                                    {book?.selectedCurrentUserAssign?.assTypeId === 31 && " Write Report (External)"}
                                                    {book?.selectedCurrentUserAssign?.assTypeId === 32 && " Set Stars Rating"}

                                                        </span>

                                            </ListGroup.Item>
                                            <ListGroup.Item className="py-1 px-2 fst-italic mt-0 text-muted">
                                                <FaTasks className="me-2" />
                                                To Do: {Number(book.selectedCurrentUserAssign?.assTypeId) === 32
                                                    ? "Leave star rating"
                                                    : (book.selectedCurrentUserAssign?.assignment_type || 'No type available')}
                                            </ListGroup.Item>
                                            <ListGroup.Item className="py-1 px-2 fst-italic mt-0 text-muted">
                                                <FaMoneyBillWave className="me-2" />
                                                Spends:
                                                <b className="text-danger">
                                                    {[10,11,12,13,14,15,16,17,18,19,20,21,22,23,24,25,26,27].includes(book.selectedCurrentUserAssign?.assTypeId) ? (
                                                        book.selectedCurrentUserAssign?.assignment_price_publisher
                                                            ? ` $${book.selectedCurrentUserAssign?.book_cost_compensation} (Rebatable)`
                                                            : "N/A"
                                                    ) : (
                                                        (() => {
                                                            let priceLabel;
                                                            switch (book.selectedCurrentUserAssign?.booksPriceId) {
                                                                case 1:
                                                                    priceLabel = " Free";
                                                                    break;
                                                                case 2:
                                                                    priceLabel = " $0.99";
                                                                    break;
                                                                case 3:
                                                                    priceLabel = " $1.99";
                                                                    break;
                                                                case 4:
                                                                    priceLabel = " $2.99";
                                                                    break;
                                                                case 5:
                                                                    priceLabel = " $3.99";
                                                                    break;
                                                                case 6:
                                                                    priceLabel = " $4.99";
                                                                    break;
                                                                case 7:
                                                                    priceLabel = " $5.99";
                                                                    break;
                                                                case 8:
                                                                    priceLabel = " $6.99";
                                                                    break;
                                                                case 9:
                                                                    priceLabel = " $7.99";
                                                                    break;
                                                                case 10:
                                                                    priceLabel = " $8.99";
                                                                    break;
                                                                case 11:
                                                                    priceLabel = " $9.99";
                                                                    break;
                                                                default:
                                                                    priceLabel = " Unknown Type";
                                                            }

                                                            // Добавляем " Promo" при нужных условиях
                                                            if (book.selectedCurrentUserAssign?.booksPriceId === 1 && [3, 4, 5, 6, 32].includes(book.selectedCurrentUserAssign?.assTypeId)) {
                                                                priceLabel += " Promo";
                                                            }

                                                            return priceLabel;
                                                        })()
                                                    )}




                                                </b>
                                            </ListGroup.Item>
                                        </ListGroup>
                                    </Col>
                                    <Col xs={12} md={4}>
                                        <ListGroup variant="success">
                                            <ListGroup.Item className="py-1 px-2 fst-italic mt-0 text-muted">
                                                <FaBookOpen className="me-2" />
                                                Complete in: {daysToRead} Day{daysToRead >= 1 ? "s" : ""}. {daysLeft === 0 ?
                                                ( <> {(book.selectedCurrentUserAssign?.assignment_status === 'READING' ||
                                                    book.selectedCurrentUserAssign?.assignment_status === 'READING_RIDT' ||
                                                    book.selectedCurrentUserAssign?.assignment_status === 'READING_RE') ? "Expired" : '' }</> ) :
                                                (<><strong>Left: {daysLeft} Day{daysLeft >= 1 ? "s" : ""}</strong></>)}
                                            </ListGroup.Item>
                                            <ListGroup.Item className="py-1 px-2 fst-italic mt-0 text-muted">
                                                <FaCoins className="me-2" />
                                                Earn: {book.selectedCurrentUserAssign?.assignment_price_reader} Virt
                                                {book.selectedCurrentUserAssign?.received_bonus > 0 && ` + ${book.selectedCurrentUserAssign?.received_bonus} Virt`}
                                            </ListGroup.Item>
                                            <ListGroup.Item className="py-1 px-2 fst-italic mt-0 text-muted">
                                                <FaHourglassStart className="me-2" />
                                                Start: {`${startreadmonth}-${startreadday}`}
                                            </ListGroup.Item>
                                        </ListGroup>
                                    </Col>
                                    <Col xs={12} md={3}>
                                        <ListGroup variant="success">
                                            <ListGroup.Item className="py-1 px-2 fst-italic mt-0 text-muted">
                                                <FaGlobe className="me-2" />
                                                {book.selectedCurrentUserAssign?.book?.language}
                                            </ListGroup.Item>
                                            <ListGroup.Item className="py-1 px-2 fst-italic mt-0 text-muted">
                                                {book.selectedCurrentUserAssign?.book?.book_type === 'product' ? (
                                                    <>
                                                        <FaTags className="me-2" />
                                                    Amazon FBA Product
                                                    </>
                                                ):(
                                                    <>
                                                        <FaFileWord className="me-2" />
                                                        {book.selectedCurrentUserAssign?.book?.total_words} Words
                                                    </>
                                                )}

                                            </ListGroup.Item>
                                            <ListGroup.Item className="py-1 px-2 fst-italic mt-0 text-muted">
                                                <FaListOl className="me-2" />
                                                {(() => {
                                                    switch (book.selectedCurrentUserAssign?.assignment_status) {
                                                        case 'READING': return <strong>Working</strong>;
                                                        case 'READING_RIDT': return <strong>Due Today</strong>;
                                                        case 'READING_RV': return <strong>Post ASAP</strong>;
                                                        case 'REPORTED': return <strong>Reported</strong>;
                                                        case 'REPORTED_RFU': return <strong>Follow Up</strong>;
                                                        case 'REPORTED_RV': return <strong>Check Review</strong>;
                                                        case 'REPORTED_EXP': return <strong>Expired</strong>;
                                                        case 'SUCCESS': return <strong>Confirmed</strong>;
                                                        case 'SUSPENDED': return <strong>Suspended</strong>;
                                                        default: return <strong>Unknown Status</strong>;
                                                    }
                                                })()} ({book.selectedCurrentUserAssign?.reviewer_reading_step})
                                            </ListGroup.Item>
                                        </ListGroup>
                                    </Col>
                                </Row>

                                {selectedReviewerSnapshotName && (
                                    <div className="mt-2 px-2 py-1 border rounded bg-light text-muted">
                                        <FaBell className="me-2 text-success" />
                                        Reviewer name for this assignment:
                                        <strong className="ms-1 text-success">{selectedReviewerSnapshotName}</strong>
                                    </div>
                                )}

                            </Col>
                        </Row>
                        <Row className="align-items-center mt-3">
                            <Col xs={12} md={10} className="mb-2 mb-md-0">
                                {(() => {
                                    switch (book.selectedCurrentUserAssign?.assignment_status) {
                                        case 'READING':
                                            return (
                                                <div className="d-flex flex-column flex-md-row align-items-start align-items-md-center text-muted">
            <span className="me-md-2 mb-2 mb-md-0 ">
               Time remaining to Turn In this Assignment:
                <CountdownValue
                    label=" "
                    targetMinutes={readingDeadlineMinutes}
                    className="text-success"
                />
</span>
                                                </div>
                                            );
                                        case 'READING_RIDT':
                                            return (
                                                <div className="text-muted">
                                                    Your Assignment is Due Today. Please, write and post your review as soon as possible.
                                                    <CountdownValue
                                                        label=" Deadline: "
                                                        targetMinutes={readingDeadlineMinutes}
                                                    />
                                                </div>
                                            );
                                        case 'READING_RE':
                                            if (isProofRequiredAssignment(book.selectedCurrentUserAssign?.assTypeId)) {
                                                return (
                                                    <div className="text-muted">
                                                        The deadline has passed. Submit the required proof before the grace period ends, or this assignment will be canceled and your Virt/bonus will be deducted.
                                                        <CountdownValue
                                                            label=" Grace period left: "
                                                            targetMinutes={autoTurnInDeadlineMinutes}
                                                        />
                                                    </div>
                                                );
                                            }
                                            return (
                                                <div className="text-muted">
                                                    The deadline has passed. You have an extra 24 hours to complete and submit your review.
                                                    <CountdownValue
                                                        label=" Auto Turn In in: "
                                                        targetMinutes={autoTurnInDeadlineMinutes}
                                                    />
                                                </div>
                                            );
                                        case 'REPORTED':
                                            if (isProofRequiredAssignment(book.selectedCurrentUserAssign?.assTypeId)) {
                                                return (
                                                    <div className="text-muted">
                                                        Great! Our team will review the files you uploaded as soon as possible to verify your submission.
                                                    </div>
                                                );
                                            } else {
                                                return (
                                                    <div className="text-muted">
                                                        This assignment has been turned in (manually or automatically).
                                                        We are now waiting for your review to appear on Amazon.
                                                        <CountdownValue
                                                            label=" Review go-live deadline: "
                                                            targetMinutes={reviewVisibilityDeadlineMinutes}
                                                            className="text-success"
                                                        />
                                                    </div>
                                                );
                                            }
                                        case 'REPORTED_RFU':
                                            return (
                                                <div className="text-muted">
                                                    It has been 8 days since you reported. Check if your review is live.
                                                    <CountdownValue
                                                        label=" Review go-live deadline: "
                                                        targetMinutes={reviewVisibilityDeadlineMinutes}
                                                    />
                                                </div>
                                            );
                                        case 'REPORTED_RV':
                                            return (
                                                <div className="text-muted">
                                                    More than 12 days have passed.
                                                    <CountdownValue
                                                        label=" Time left before Virt deduction: "
                                                        targetMinutes={reviewVisibilityDeadlineMinutes}
                                                    />
                                                </div>
                                            );
                                        case 'REPORTED_EXP':
                                            return (
                                                <div className="text-muted">
                                                    Virt already deducted. Confirm during grace period to get them back.
                                                    <CountdownValue
                                                        label=" Grace period left: "
                                                        targetMinutes={reportedExpGraceDeadlineMinutes}
                                                    />
                                                </div>
                                            );
                                        case 'SUCCESS':
                                            return <div className="text-muted">This assignment is completed successfully. Thank you!</div>;
                                        default:
                                            return null;
                                    }
                                })()}
                            </Col>

                            <Col
                                xs={12}
                                md={2}
                                className="d-flex flex-column justify-content-end align-items-end"
                            >
                                {(currentStep === 'STEP1' ||
                                        currentStep === 'STEP2' ||
                                        currentStep === 'STEP3' ||
                                        currentStep === 'STEP4') &&
                                    book?.selectedCurrentUserAssign?.assTypeId !== 8 && (
                                        <Button
                                            variant="secondary"
                                            className="shadow-sm btn-sm d-flex justify-content-center align-items-center fw-light mt-auto"
                                            style={{
                                                paddingLeft: '2rem',
                                                paddingRight: '0.75rem',
                                                position: 'relative',
                                                width: 'fit-content'
                                            }}
                                            onMouseEnter={() => setHoveredDel('delAss')}
                                            onMouseLeave={() => setHoveredDel(null)}
                                            onClick={() => setShowCancelAssignment(true)}
                                        >
                                            <FaTrashAlt
                                                className="position-absolute"
                                                style={{
                                                    left: '0.55rem',
                                                    top: '50%',
                                                    transform: 'translateY(-50%)'
                                                }}
                                            />
                                            Cancel?
                                        </Button>
                                    )}
                            </Col>

                        </Row>
                    </Card.Body>
                </Card>



                <Row className="p-0 ps-1 pe-1 mt-0 mb-0">
                {/* Первая колонка */}
                    <Col xs={12} sm={6} lg={3} className="mb-0 p-2">
                        <Card className={`h-100 rounded ${currentStep === 'STEP1' ? 'border shadow-sm border-secondary' : ''}`}>
                            <Card.Body className="d-flex flex-column p-2 position-relative">
                                {/* Иконка в углу */}
                                {assignmentData.icon1}
                                <FaArrowRight className="text-success position-absolute bottom-0 end-0 m-2" size={18} />

                                {/* Верхняя часть (текст) */}
                                <div className="flex-grow-1">
                                    <strong>
                                        <p className="small text-muted fst-italic mb-0">{assignmentData.title1}</p>
                                    </strong>
                                    <p className="small text-muted fst-italic mb-1">{assignmentData.description1}</p>
                                </div>

                                {/* Нижняя часть (кнопки) */}
                                <div className="mt-auto">
                                    {(book.selectedCurrentUserAssign?.assTypeId === 1 && currentStep !== 'STEP1') && (
                                        <div className="d-flex gap-2 align-items-center mb-0">
                                            <Button
                                                variant="outline-success"
                                                size="sm"
                                                className="d-inline-flex align-items-center"
                                                onClick={handleDownload}
                                                disabled={loading}
                                            >
                                                {loading ? (
                                                    <>
                                                        <span className="spinner-border spinner-border-sm me-2" role="status" />
                                                        Downloading...
                                                    </>
                                                ) : (
                                                    <>
                                                        <FaBook className="me-2" /> Download Book Again
                                                    </>
                                                )}
                                            </Button>
                                        </div>
                                    )}

                                    {(book.selectedCurrentUserAssign?.assTypeId === 7 && currentStep !== 'STEP1') && (
                                        <div className="d-flex gap-2 align-items-center mb-0">
                                            <Button
                                                variant="outline-success"
                                                size="sm"
                                                className="d-inline-flex align-items-center"
                                                onClick={() => openRedirectedLink(book.selectedCurrentUserAssign?.book?.audio_download_link_ned)}
                                                disabled={loading}
                                            >
                                                {loading ? (
                                                    <>
                                                        <span className="spinner-border spinner-border-sm me-2" role="status" />
                                                        Downloading...
                                                    </>
                                                ) : (
                                                    <>
                                                        <FaBook className="me-2" /> Download Audiobook
                                                    </>
                                                )}
                                            </Button>
                                        </div>
                                    )}

                                    {(
                                        [2, 3, 4, 9, 10, 12, 13, 14, 16, 17, 18, 20, 22, 24, 25, 26].includes(book.selectedCurrentUserAssign?.assTypeId) &&
                                        ['STEP2', 'STEP3', 'STEP4', 'STEP5'].includes(currentStep)
                                    ) && (
                                        <div className="d-flex gap-2 align-items-center mb-0">
                                            <Button
                                                variant="outline-success"
                                                size="sm"
                                                className="d-inline-flex align-items-center"
                                                onClick={() => openRedirectedLink(`${String(user?.user?.amz_country)}/dp/${assignmentData?.book_asin}`)}

                                            >
                                                <FaBook className="me-2" /> {[7, 8, 9, 10].includes(book.selectedCurrentUserAssign?.booksTypeId)
  ? "View Product"
  : "View Book"}
                                            </Button>
                                        </div>
                                    )}
                                    {(
                                        [28,29,30,31,32].includes(book.selectedCurrentUserAssign?.assTypeId) &&
                                        ['STEP2', 'STEP3', 'STEP4', 'STEP5'].includes(currentStep)
                                    ) && (
                                        <div className="d-flex gap-2 align-items-center mb-0">
                                            <Button
                                                variant="outline-success"
                                                size="sm"
                                                className="d-inline-flex align-items-center"
                                                onClick={() =>
                                                    Number(book.selectedCurrentUserAssign?.assTypeId) === 32
                                                        ? openAssignmentBookPage()
                                                        : openRedirectedLink(`${assignmentData.review_link}`)
                                                }
                                            >
                                                <FaStar className="me-2" />
                                                {Number(book.selectedCurrentUserAssign?.assTypeId) === 32 ? "View Kindle Book" : "View Review"}
                                            </Button>
                                        </div>
                                    )}



                                    {(book.selectedCurrentUserAssign?.assignment_search_term && currentStep !== 'STEP1') && (
                                        <div className="d-flex gap-2 align-items-center mb-1">
                                            <Button
                                                variant="outline-success"
                                                size="sm"
                                                className="d-inline-flex align-items-center"
                                                onClick={() => {
                                                    const searchQuery = book.selectedCurrentUserAssign?.assignment_search_term?.trim();
                                                    if (!searchQuery) {
                                                        alert("No search term provided.");
                                                        return;
                                                    }

                                                    const bookType = book.selectedCurrentUserAssign?.booksTypeId || 1;
                                                    const base = user?.user?.amz_country || "https://www.amazon.com";

                                                    // 🗺️ Карта категорий по типам
                                                    const indexMap = {
                                                        1: "digital-text",            // Kindle
                                                        2: "stripbooks-intl-ship",    // Paperback
                                                        3: "stripbooks-intl-ship",    // Hardcover
                                                        4: "audiobooks",              // Audible
                                                        7: "all",                     // Products
                                                        8: "all",
                                                        9: "all",
                                                        10: "all",
                                                    };

                                                    const index = indexMap[bookType] || "stripbooks";
                                                    const u = new URL("/s", base);
                                                    u.searchParams.set("k", searchQuery);

                                                    // если "all" → не добавляем параметр i=
                                                    if (index !== "all") {
                                                        u.searchParams.set("i", index);
                                                    }

                                                    openRedirectedLink(u.toString());
                                                }}
                                            >
                                                <FaBook className="me-2" /> {[7, 8, 9, 10].includes(book.selectedCurrentUserAssign?.booksTypeId)
                                                ? "Find Product"
                                                : "Find Book"}
                                            </Button>

                                        </div>
                                    )}
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>

                    {/* Вторая колонка */}
                    <Col xs={12} sm={6} lg={3} className="mb-0 p-2">
                        <Card className={`h-100 rounded ${currentStep === 'STEP2' ? 'border shadow-sm border-secondary' : ''}`}>
                            <Card.Body className="d-flex flex-column p-2 position-relative">
                                {/* Иконка в углу */}
                                {assignmentData.icon2}

                                {/* Стрелка справа внизу */}
                                <FaArrowRight className="text-success position-absolute bottom-0 end-0 m-2" size={18} />

                                {/* Верхняя часть (текст) */}
                                <div className="flex-grow-1">
                                    <strong>
                                        <p className="small text-muted fst-italic mb-0">{assignmentData.title2}</p>
                                    </strong>
                                    <p className="small text-muted fst-italic mb-1">{assignmentData.description2}</p>
                                </div>

                                {/* Нижняя часть (кнопки) */}
                                <div className="mt-auto">
                                {/* Кнопки */}
                                    {(['STEP3', 'STEP4', 'STEP5'].includes(currentStep)) &&
                                        ([1,2,3,4,5,6,9].includes(book.selectedCurrentUserAssign?.assTypeId)) && (
                                           <div className=" gap-2">
                                        <Button
                                            variant="outline-success"
                                            size="sm"
                                            className="d-inline-flex align-items-center"
                                            onClick={() => openRedirectedLink(`${String(user?.user?.amz_country)}/dp/${assignmentData?.book_asin}`)}
                                        >
                                            <FaBook className="me-2" /> {[7, 8, 9, 10].includes(book.selectedCurrentUserAssign?.booksTypeId)
  ? "View Product"
  : "View Book"}
                                        </Button>

                                        {(book.selectedCurrentUserAssign?.assTypeId === 4 || book.selectedCurrentUserAssign?.assTypeId === 6) && (
                                            <Button
                                                variant="outline-success"
                                                size="sm"
                                                className="d-inline-flex ms-2 align-items-center"
                                                onClick={handleDownloadPhoto}
                                                disabled={loading}
                                            >
                                                {loading ? (
                                                    <>
                                                        <span className="spinner-border spinner-border-sm me-2" role="status" />
                                                        Downloading...
                                                    </>
                                                ) : (
                                                    <>
                                                        <FaImage className="me-2" /> Photo
                                                    </>
                                                )}
                                            </Button>
                                        )}
                                    </div>
                                )}

                                {(currentStep === 'STEP3' || currentStep === 'STEP4' || currentStep === 'STEP5') && book.selectedCurrentUserAssign?.assTypeId === 8 && (
                                    <div className=" gap-2">
                                        <Button
                                            variant="outline-success"
                                            size="sm"
                                            className="d-inline-flex ms-2 align-items-center"
                                            onClick={() => openRedirectedLink(`${String(user?.user?.audible_country)}/pd/${book.selectedCurrentUserAssign?.book?.audio_audible_asin}`)}
                                        >
                                            <FaBook className="me-2" /> View Audiobook
                                        </Button>
                                    </div>
                                )}
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>

                    {/* Третья колонка */}
                    <Col xs={12} sm={6} lg={3} className="mb-0 p-2">
                        <Card className={`h-100 rounded ${currentStep === 'STEP3' ? 'border shadow-sm border-secondary' : ''}`}>
                            <Card.Body className="d-flex flex-column p-2 position-relative">
                                {/* Иконка в углу */}
                                {assignmentData.icon3}

                                {/* Стрелка справа внизу */}
                                <FaArrowRight className="text-success position-absolute bottom-0 end-0 m-2" size={18} />

                                {/* Верхняя часть (текст) */}
                                <div className="flex-grow-1">
                                    <strong>
                                        <p className="small text-muted fst-italic mb-0">{assignmentData.title3}</p>
                                    </strong>
                                    <p className="small text-muted fst-italic mb-1">{assignmentData.description3}</p>
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>

                    {/* четвёртая колонка */}
                    <Col xs={12} sm={6} lg={3} className="mb-0 p-2">
                        <Card className={`h-100 rounded ${currentStep === 'STEP4' ? 'border shadow-sm border-secondary' : ''}`}>
                            <Card.Body className="d-flex flex-column p-2 position-relative">
                                {/* Иконка в углу */}
                                {assignmentData.icon4}

                                {/* Верхняя часть (текст) */}
                                <div className="flex-grow-1">
                                    <strong>
                                        <p className="small text-muted fst-italic mb-0">{assignmentData.title4}</p>
                                    </strong>
                                    <p className="small text-muted fst-italic mb-1">{assignmentData.description4}</p>
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>

                </Row>


            {/* Блок 1:  шаг 0 */}
            {(book.selectedCurrentUserAssign?.reviewer_reading_step === 'STEP1') && (
                    <>
                 <Row className="p-0 ps-2 pe-2 mt-0 mb-2 small">
                    <Col lg={12} className="mb-0 mt-0 p-0 ">
                        {/* Верхний ряд с текстом */}
                        <Card className="h-100 rounded p-1">
                            <Card.Body className="d-flex flex-column p-2 position-relative">
                        <div>
                            <h5 className="mx-auto text-muted mb-1"
                                style={{ marginBottom: '-10px', whiteSpace: 'pre-line' }}>
                                {assignmentData.full_title1}
                            </h5>

                            <p className="mb-2 text-muted">
                                <strong> {assignmentData.full_subtitle1} </strong>
                            </p>
                            <p className="mb-0 text-muted">
                                {assignmentData.full_description1}
                             </p>
                         </div>

                        {(book.selectedCurrentUserAssign?.assTypeId === 1 && book.selectedCurrentUserAssign?.book?.book_download) && (
                            <div className="d-flex justify-content-between align-items-center mt-0">
                                {/* Кнопка "Download Book" слева */}
                                <button
                                    onClick={handleDownload}
                                    className="btn btn-sm btn-success d-flex align-items-center"
                                    disabled={loading} // Блокируем кнопку при загрузке
                                >
                                    {loading ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-2" role="status"/>
                                            Downloading...
                                        </>
                                    ) : (
                                        <>
                                            <FaBook className="me-2"/> Download Book
                                        </>
                                    )}
                                </button>

                                {/* Кнопка "Step Done" справа */}
                                <Button
                                    variant="success"
                                    className="d-flex btn-sm align-items-center"
                                    onClick={() => handleDoneStep('readBook', 'STEP2')}
                                >
                                    Next Step<FaArrowRight className="ms-2"/>
                                </Button>
                            </div>
                        )}

                        {(book.selectedCurrentUserAssign?.assTypeId === 7) && (
                            <div className="d-flex justify-content-between align-items-center mt-3">
                                {/* Кнопка "Download Book" слева */}
                                <div>
                                   {/* Кнопка открытия аудиокниги по ссылке от пользователя */}
                                    <Button
                                        variant="success"
                                        className="btn btn-success btn-sm d-flex align-items-center mt-2"
                                        onClick={() => openRedirectedLink(book?.selectedCurrentUserAssign?.book?.audio_download_link_ned)}
                                    >
                                        <FaBook className="me-2"/> Download Audiobook
                                    </Button>
                                </div>

                                {/* Кнопка "Step Done" справа */}
                                <Button
                                    variant="success"
                                    className="d-flex btn btn-sm align-items-center"
                                    onClick={() => handleDoneStep('readBook', 'STEP2')}
                                >
                                    Next Step<FaArrowRight className="ms-2"/>
                                </Button>
                            </div>
                        )}

                        {(book.selectedCurrentUserAssign?.assTypeId === 8) && (
                            <>
                                 <div className="d-flex justify-content-between align-items-center mt-2">
                                    {/* Кнопка "Download Book" слева */}

                                    <div className="d-flex gap-2 mt-2 ">
                                        {/* Кнопка открытия аудиокниги по ссылке от пользователя */}
                                        <Button
                                            variant="success"
                                            className="btn btn-sm btn-success d-flex align-items-center mt-0"
                                            onClick={() => openRedirectedLink(`${String(user?.user?.audible_country)}/pd/${book.selectedCurrentUserAssign?.book?.audio_audible_asin}`)}
                                        >
                                            <FaBook className="me-2"/> View Audiobook
                                        </Button>
                                        {book.selectedCurrentUserAssign?.audio_promo_code && (
                                            <CopyButton text={book.selectedCurrentUserAssign?.audio_promo_code} label="Copy Promo Code"/>
                                        )}
                                    </div>


                                    {/* Кнопка "Step Done" справа */}
                                    <Button
                                        variant="success"
                                        className="btn d-flex btn-sm align-items-center"
                                        onClick={() => handleDoneStep('readBook', 'STEP2')}
                                    >
                                        Next Step<FaArrowRight className="ms-2"/>
                                    </Button>
                                </div>

                            </>
                        )}

                                {[2, 3, 4, 9, 10, 12, 13, 14, 16, 17, 18, 20, 22, 24, 25, 26].includes(book.selectedCurrentUserAssign?.assTypeId) && (
                                    <div className="d-flex justify-content-between align-items-center mt-0">
                                <div className="mt-0">
                                {/* Кнопка открытия книги на Amazon */}
                                    <Button
                                        variant="success"
                                        className="btn btn-sm btn-success d-flex align-items-center mt-0"
                                        onClick={() => openRedirectedLink(`${String(user?.user?.amz_country)}/dp/${assignmentData.book_asin}`)}
                                    >
                                        <FaBook className="me-2"/> {[7, 8, 9, 10].includes(book.selectedCurrentUserAssign?.booksTypeId)
  ? "View Product"
  : "View Book"}
                                    </Button>
                                </div>

                                {/* Кнопка "Step Done" справа */}
                                <Button
                                    variant="success"
                                    className="btn d-flex btn-sm align-items-center"
                                    onClick={() => handleDoneStep('readBook', 'STEP2')}
                                >
                                    Next Step<FaArrowRight className="ms-2"/>
                                </Button>
                            </div>
                        )}
                                {[11, 15, 19, 21, 23, 27].includes(book.selectedCurrentUserAssign?.assTypeId) && (
                                    <>
                                    <div className="d-flex justify-content-between align-items-center mt-0">

                                        <div>
                                            {/* Кнопка открытия книги на Amazon */}
                                            <Button
                                                variant="success"
                                                size="sm"
                                                className="d-flex align-items-center w-100"
                                                onClick={() => {
                                                    const searchQuery = book.selectedCurrentUserAssign?.assignment_search_term?.trim();
                                                    if (!searchQuery) {
                                                        alert("No search term provided.");
                                                        return;
                                                    }

                                                    const bookType = book.selectedCurrentUserAssign?.booksTypeId || 1;
                                                    const base = user?.user?.amz_country || "https://www.amazon.com";

                                                    // 🗺️ Категории по типам
                                                    const indexMap = {
                                                        1: "digital-text",           // Kindle
                                                        2: "stripbooks-intl-ship",   // Paperback
                                                        3: "stripbooks-intl-ship",   // Hardcover
                                                        4: "audiobooks",             // Audible
                                                        7: "all",                    // Product
                                                        8: "all",
                                                        9: "all",
                                                        10: "all",
                                                    };

                                                    const index = indexMap[bookType] || "stripbooks";
                                                    const u = new URL("/s", base);
                                                    u.searchParams.set("k", searchQuery);

                                                    // если категория "all" → не добавляем параметр i=
                                                    if (index !== "all") {
                                                        u.searchParams.set("i", index);
                                                    }

                                                    openRedirectedLink(u.toString());
                                                }}
                                            >
                                                <FaTags className="me-2" />
                                                {[7, 8, 9, 10].includes(book.selectedCurrentUserAssign?.booksTypeId)
                                                    ? "Find Product by Search Term"
                                                    : "Find Book by Search Term"}
                                            </Button>

                                        </div>


                                        {/* Кнопка "Step Done" справа */}
                                        <Button
                                            variant="success"
                                            className="btn d-flex btn-sm align-items-center"
                                            onClick={() => handleDoneStep('readBook', 'STEP2')}
                                        >
                                            Next Step<FaArrowRight className="ms-2"/>
                                        </Button>


                                    </div>
                                    <div className="mt-3">
                                    <span>
                                    Find this book/product on Amazon with the author’s search term: <strong className="text-muted fst-italic">"{book.selectedCurrentUserAssign?.assignment_search_term}"</strong> and then buy it.
                        </span>
                    </div>

                     </>
                                )}
                                {[28,29,30,31,32].includes(book.selectedCurrentUserAssign?.assTypeId) && (
                                    <div className="d-flex justify-content-between align-items-center mt-0">
                                        <div className="mt-0">
                                            {/* Кнопка открытия на Amazon */}
                                            <Button
                                                variant="success"
                                                className="btn btn-sm btn-success d-flex align-items-center mt-0"
                                                onClick={() =>
                                                    Number(book.selectedCurrentUserAssign?.assTypeId) === 32
                                                        ? openAssignmentBookPage()
                                                        : openRedirectedLink(`${assignmentData.review_link}`)
                                                }
                                            >
                                                <FaStar className="me-2"/>
                                                {Number(book.selectedCurrentUserAssign?.assTypeId) === 32 ? "Open Kindle Book" : "Open Review"}
                                            </Button>
                                        </div>

                                        {/* Кнопка "Step Done" справа */}
                                        <Button
                                            variant="success"
                                            className="btn d-flex btn-sm align-items-center"
                                            onClick={() => handleDoneStep('readBook', 'STEP2')}
                                        >
                                            Next Step<FaArrowRight className="ms-2"/>
                                        </Button>
                                    </div>
                                )}

                        {![11, 15, 19, 21, 23, 27].includes(book.selectedCurrentUserAssign?.assTypeId) && book.selectedCurrentUserAssign?.assignment_search_term && (
<>

                                       {/* Две колонки под текстом */}
                                 <Row className="g-0 mt-0 w-100">
                                      <Col md={12} className="p-0">


                                            {book.selectedCurrentUserAssign?.assignment_search_term ? (
                                                showExtractor ? (
                                                    <>
                                                    <span>
                                              Find this book on Amazon with the author’s search term: <strong className="text-muted fst-italic">"{book.selectedCurrentUserAssign?.assignment_search_term}"</strong>.
                                               <br/> Just tap the button below and follow the instructions.
                                                </span>

                                                    <Card className="mt-2 g-0 p-3 shadow-sm rounded w-100">

                                                        <Row className="g-2 gap-2 align-items-center">
                                                            <Col xs={12} md="auto">
                                                                <Button
                                                                    variant="success"
                                                                    className="btn btn-sm btn-success d-flex align-items-center w-100"
                                                                    onClick={() => {
                                                                        const searchQuery = book.selectedCurrentUserAssign?.assignment_search_term?.trim();
                                                                        if (!searchQuery) {
                                                                            alert("No search term provided.");
                                                                            return;
                                                                        }

                                                                        const bookType = book.selectedCurrentUserAssign?.booksTypeId || 1;
                                                                        const indexMap = {
                                                                            1: "digital-text",
                                                                            2: "stripbooks-intl-ship",
                                                                            3: "stripbooks-intl-ship",
                                                                            4: "audiobooks",
                                                                            7: "",
                                                                        };

                                                                        const base = user?.user?.amz_country || "https://www.amazon.com";
                                                                        const u = new URL("/s", base);
                                                                        u.searchParams.set("k", searchQuery);
                                                                        u.searchParams.set("i", indexMap[bookType] || "stripbooks");

                                                                        openRedirectedLink(u.toString());
                                                                    }}

                                                                >
                                                                    <FaBook className="me-2" /> Find Book by Search Term
                                                                </Button>
                                                            </Col>

                                                            <Col xs={12} md>
                                                                <AmazonLinkParser onExtract={handleExtract} />
                                                            </Col>
                                                        </Row>
                                                    </Card>
                                                    </>
                                                ) : (
                                                    <Alert variant="success" className="mt-3">
                                                        ✅ ASIN & Keywords successfully verified! Now you can buy the book and start reading.
                                                        Your balance has just been increased by 50% of this assignment's cost!
                                                        Go to the next step. You're doing great, keep up the good work!
                                                    </Alert>
                                                )
                                            ) : (
                                                <p className="text-muted">✅ Already Verified</p>
                                            )}
                                        </Col>

                                    </Row>
                                       {currentStep === "STEP1" && showExtractor && (
                                           <>
                                    <Row className="mt-3">

                                        <Col md={8} className="border-start d-flex align-items-top justify-content-center">

                                                {/* Верхний ряд с текстом */}
                                                <div className="d-flex flex-wrap align-items-top text-muted">

                                                <p className="mb-2">
                                                    <h6 className="fw-bold mt-0">How to Find and Confirm the URL:</h6>

                                                    Tap the “Find Book by Search Term” button, then <strong>open the book (dive in) and copy the full URL</strong> from your browser’s address bar.
                                                    Next, paste it into this field and hit the “Check” button.
                                                    Once that’s done, you’re all set to purchase the book and start reading. (No worries — this field works perfectly!)     </p>

                                            <img
                                                src="/img/site_img/gif_confirm_buy_link.gif"
                                                alt="Scale and Format Instruction"
                                                style={{
                                                    width: '100%',
                                                    maxWidth: '650px',
                                                    height: 'auto',
                                                    borderRadius: '4px',
                                                    border: '1px solid #ccc',
                                                    boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
                                                }}
                                            />
                                        </div>

                                        </Col>
                                        <Col md={4} className="mt-0">
                                            <div className="text-muted">
                                                <h6 className="fw-bold mt-0">Instruction:</h6>
                                                <ListGroup variant="success" className="text-muted fst-italic">
                                                    <ListGroup.Item
                                                        style={{
                                                            paddingTop: '0.20rem', // Уменьшение высоты строки
                                                            paddingBottom: '0.20rem', // Уменьшение высоты строки
                                                            marginRight: '10px',
                                                        }}
                                                    >
                                                        Step 1: Open the Amazon by link above and find this book.</ListGroup.Item>
                                                    <ListGroup.Item
                                                        style={{
                                                            paddingTop: '0.20rem', // Уменьшение высоты строки
                                                            paddingBottom: '0.20rem', // Уменьшение высоты строки
                                                            marginRight: '10px',
                                                        }}
                                                    >
                                                        Step 2: Dive in Book then Copy the full URL from
                                                        the address bar.</ListGroup.Item>
                                                    <ListGroup.Item
                                                        style={{
                                                            paddingTop: '0.20rem', // Уменьшение высоты строки
                                                            paddingBottom: '0.20rem', // Уменьшение высоты строки
                                                            marginRight: '10px',
                                                        }}
                                                    >
                                                        Step 3: Paste the Just Copied Huge link into the
                                                        input field.</ListGroup.Item>
                                                    <ListGroup.Item
                                                        style={{
                                                            paddingTop: '0.20rem', // Уменьшение высоты строки
                                                            paddingBottom: '0.20rem', // Уменьшение высоты строки
                                                            marginRight: '10px',
                                                        }}
                                                    >
                                                        Step 4: Click "Check" to
                                                        verify and Get a second +50% to Balance! You can Buy Book Now!</ListGroup.Item>
                                                </ListGroup>

                                            </div>
                                        </Col>

                                    </Row>
                                           </>
                                       )}

    <Row className="mt-3">
        <Col className="d-flex justify-content-end">
            <Button
                variant="success"
                className="btn btn-sm d-flex align-items-center"
                onClick={() => handleDoneStep('readBook', 'STEP2')}
                disabled={showExtractor === true}
            >
                Next Step <FaArrowRight className="ms-2" />
            </Button>
        </Col>
    </Row>



</>
                        )}
                            </Card.Body>
                        </Card>

                    </Col>
                </Row>

                    </>
            )}


            {/* Блок 2: Read the book – кнопки видны, если currentStep === "STEP2" */}
            { (currentStep === 'STEP2' || currentStep === 'STEP1') && (
                <>
                    <Row className="p-0 ps-2 pe-2 mt-0 mb-2 small">
                        <Col md={12} className="mb-0 mt-0 p-0 ">
                            {/* Верхний ряд с текстом */}
                            <Card className="h-100 rounded p-1">
                                <Card.Body className="d-flex flex-column p-2 position-relative">
                            <div>
                                <h5 className="mx-auto text-muted mb-1"
                                    style={{ marginBottom: '-10px', whiteSpace: 'pre-line' }}>
                                    {assignmentData.full_title2}
                                </h5>

                                <p className="mb-1 text-muted">
                                    <strong>{assignmentData.full_subtitle2}</strong>
                                </p>

                                {[4, 6].includes(book.selectedCurrentUserAssign?.assTypeId) && (
                                    <img
                                        src={`${process.env.REACT_APP_API_URL}/photorev/${book.selectedCurrentUserAssign?.assignment_img1}`}
                                        alt="Preview"
                                        style={{
                                            width: "70px",
                                            height: "70px",
                                            objectFit: "cover",
                                            borderRadius: "8px",
                                            cursor: "pointer",
                                            float: "left", // Обтекание текста слева
                                            marginRight: "15px", // Отступ справа для текста

                                        }}
                                        onClick={handleDownloadPhoto}
                                    />
                                )}

                                <p className="mb-0 text-muted">{assignmentData.full_description2}</p>
                                {Number(book.selectedCurrentUserAssign?.assTypeId) === 32 && (
                                    <SetStarsRatingGuide className="mb-2" />
                                )}
                            </div>

                                    {([10,11,14,15, 18, 19, 20, 21, 22, 23, 24, 27].includes(book.selectedCurrentUserAssign?.assTypeId) && currentStep === 'STEP2') && (
                                        <>
                                            <h6 className="fst-italic mb-2 mt-0 text-muted">
                                                Upload a screenshot of your Amazon purchase receipt and a photo of the physical book/product:
                                            </h6>

                                            <motion.div
                                                initial={{ opacity: 0, y: 20 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                transition={{ duration: 0.4 }}
                                            >
                                                <div className="d-flex flex-column align-items-center">
                                                    {isLoading ? <Loader /> : <ProofUploadDual onFilesUpload={handleFileUpload} />}
                                                </div>
                                            </motion.div>
                                        </>
                                    )}
                                    {([28,29,30,31,32].includes(book.selectedCurrentUserAssign?.assTypeId) && currentStep === 'STEP2') && (
                                        <>
                                            <h6 className="fst-italic mb-2 mt-0 text-muted">
                                                {Number(book.selectedCurrentUserAssign?.assTypeId) === 32
                                                    ? "Upload a Kindle/mobile screenshot that proves your completed star rating:"
                                                    : "Upload a screenshot that proves your completed action:"}
                                            </h6>
                                            <motion.div
                                                initial={{ opacity: 0, y: 20 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                transition={{ duration: 0.4 }}
                                            >
                                                <div className="d-flex flex-column align-items-center">
                                                    {isLoading ? <Loader /> : <ProofUploadSingle
                                                        fieldName="receipt"
                                                        waitingText={
                                                            Number(book.selectedCurrentUserAssign?.assTypeId) === 32
                                                                ? "Waiting for Kindle/mobile rating proof..."
                                                                : "Waiting for action proof screenshot..."
                                                        }
                                                        okText="Proof OK"
                                                        errText="Duplicate Detected"
                                                        uploadBtnText="Upload Screenshot"
                                                        reuploadBtnText="Re-Upload Screenshot"
                                                        titleText={
                                                            Number(book.selectedCurrentUserAssign?.assTypeId) === 32
                                                                ? "Upload Kindle/mobile star rating proof:"
                                                                : "Upload a screenshot that proves your completed action:"
                                                        }
                                                        noteText={
                                                            Number(book.selectedCurrentUserAssign?.assTypeId) === 32
                                                                ? "Upload one clear screenshot from the Kindle reader or mobile Kindle app after clicking No, Thank You. Staff must see the assigned book, completed star rating, and your reviewer name/account."
                                                                : "Upload one clear screenshot that proves the assigned action was completed. For Helpful/Report, show the review context and confirmation."
                                                        }
                                                        onFilesUpload={handleFileUploadSingle}
                                                    />
                                                    }
                                                </div>
                                            </motion.div>
                                        </>
                                    )}

                            {(currentStep === "STEP2" || currentStep === "STEP3" || currentStep === "STEP4") && (
                                <>
                                <div className="mt-1">
                                    <div className="row g-3">
                                        {/* Кнопка загрузки фото */}
                                        {[4, 6].includes(book.selectedCurrentUserAssign?.assTypeId) && (
                                            <div className="col-12 col-md-auto">
                                                <button
                                                    onClick={handleDownloadPhoto}
                                                    className="btn btn-success btn-sm d-flex align-items-center w-100 w-md-auto"
                                                    disabled={loading}
                                                >
                                                    {loading ? (
                                                        <>
                                                            <span className="spinner-border spinner-border-sm me-2" role="status" />
                                                            Downloading...
                                                        </>
                                                    ) : (
                                                        <>
                                                            <FaImage className="me-2" /> Download Photo for Review
                                                        </>
                                                    )}
                                                </button>
                                            </div>
                                        )}

                                        {![8,28,29,30,31,32].includes(book?.selectedCurrentUserAssign?.assTypeId) && (
                                            <>
                                            <div className="row align-items-center g-2 g-md-3">

                                                {/* ЛЕВАЯ ГРУППА: View + Post */}
                                                <div className="col-12 col-md-auto">
                                                    <div className="row g-2 justify-content-between justify-content-md-start">
                                                        <div className="col-6 col-md-auto">
                                                            <Button
                                                                variant="success"
                                                                className="btn btn-sm d-flex align-items-center"
                                                                onClick={() =>
                                                                    openRedirectedLink(`${String(user?.user?.amz_country)}/dp/${assignmentData.book_asin}`)
                                                                }
                                                            >
                                                                <FaBook className="me-2" /> {[7, 8, 9, 10].includes(book.selectedCurrentUserAssign?.booksTypeId)
  ? "View Product"
  : "View Book"}
                                                            </Button>
                                                        </div>

                                                        {![10, 11, 14, 15, 22, 23].includes(book?.selectedCurrentUserAssign?.assTypeId) && (
                                                            <div className="col-6 col-md-auto d-flex justify-content-end justify-content-md-start">
                                                                <Button
                                                                    variant="success"
                                                                    className="btn btn-sm d-flex align-items-center"
                                                                    onClick={() =>
                                                                        openRedirectedLink(
                                                                            `${String(user?.user?.amz_country)}/review/create-review/?ie=UTF8&channel=glance-detail&asin=${assignmentData.book_asin}`
                                                                        )
                                                                    }
                                                                >
                                                                    <FaBook className="me-2" /> Post Review <FaArrowRight className="ms-2" />
                                                                </Button>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>

                                                {/* ПРАВАЯ ГРУППА: свитч + текст + Next */}
                                                <div className="col-12 col-md d-flex justify-content-between justify-content-md-end align-items-center flex-wrap ms-md-auto">
                                                    <div className="d-flex align-items-center me-3 mt-1 mt-md-0 flex-grow-1 flex-md-grow-0 min-w-0">
                                                        <div className="form-check form-switch m-0">
                                                            <input
                                                                className="form-check-input bg-opacity-50 bg-success border-success text-white"
                                                                type="checkbox"
                                                                id="reviewSwitch"
                                                                style={{ transform: 'scale(1.2)' }}
                                                                onChange={handleSwitchChange}
                                                            />
                                                        </div>
                                                        <label
                                                            className="form-check-label fw-semibold ms-2 text-truncate"
                                                            htmlFor="reviewSwitch"
                                                            title={
                                                                [10, 11, 14, 15, 22, 23].includes(book?.selectedCurrentUserAssign?.assTypeId)
                                                                    ? "I confirm BuyOut"
                                                                    : "I confirm the review is posted."
                                                            }
                                                        >
                                                            {[10, 11, 14, 15, 22, 23].includes(book?.selectedCurrentUserAssign?.assTypeId) ? (
                                                                <>
      <span className="d-none d-sm-inline">
        I confirm that the information provided is true
      </span>
                                                                    <span className="d-inline d-sm-none">I confirm info is true</span>
                                                                </>
                                                            ) : (
                                                                "I confirm the review is posted."
                                                            )}
                                                        </label>


                                                    </div>

                                                    <div className="mt-2 mt-md-0 ">
                                                        <Button
                                                            variant="success"
                                                            className="btn btn-sm d-flex align-items-center justify-content-center"
                                                            onClick={() => handleDoneStep('readBook', 'STEP3')}
                                                            disabled={!isSwitchChecked}
                                                        >
                                                            Next Step <FaArrowRight className="ms-2" />
                                                        </Button>
                                                    </div>
                                                </div>

                                            </div>
                                            </>
                                        )}
                                        {[28,29,30,31,32].includes(book?.selectedCurrentUserAssign?.assTypeId) && (
                                            <>
                                                <div className="row align-items-center g-2 g-md-3">

                                                    {/* ЛЕВАЯ ГРУППА: View + Post */}
                                                    <div className="col-12 col-md-auto">
                                                        <div className="row g-2 justify-content-between justify-content-md-start">
                                                            <div className="col-6 col-md-auto">
                                                                <Button
                                                                    variant="success"
                                                                    className="btn btn-sm d-flex align-items-center"
                                                                    onClick={() =>
                                                                        Number(book.selectedCurrentUserAssign?.assTypeId) === 32
                                                                            ? openAssignmentBookPage()
                                                                            : openRedirectedLink(`${assignmentData.review_link}`)
                                                                    }
                                                                >
                                                                    <FaStar className="me-2" />
                                                                    {Number(book.selectedCurrentUserAssign?.assTypeId) === 32 ? "View Kindle Book" : "View Review"}
                                                                </Button>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    {/* ПРАВАЯ ГРУППА: свитч + текст + Next */}
                                                    <div className="col-12 col-md d-flex justify-content-between justify-content-md-end align-items-center flex-wrap ms-md-auto">
                                                        <div className="d-flex align-items-center me-3 mt-1 mt-md-0 flex-grow-1 flex-md-grow-0 min-w-0">
                                                            <div className="form-check form-switch m-0">
                                                                <input
                                                                    className="form-check-input bg-opacity-50 bg-success border-success text-white"
                                                                    type="checkbox"
                                                                    id="reviewSwitch"
                                                                    style={{ transform: 'scale(1.2)' }}
                                                                    onChange={handleSwitchChange}
                                                                />
                                                            </div>
                                                            <label
                                                                className="form-check-label fw-semibold ms-2 text-truncate"
                                                                htmlFor="reviewSwitch"
                                                                title={
                                                                    isProofRequiredAssignment(book?.selectedCurrentUserAssign?.assTypeId)
                                                                        ? "I confirm that the information provided is true"
                                                                        : "I confirm the review is posted."
                                                                }
                                                            >
                                                                {isProofRequiredAssignment(book?.selectedCurrentUserAssign?.assTypeId) ? (
                                                                    <>
      <span className="d-none d-sm-inline">
        I confirm that the information provided is true
      </span>
                                                                        <span className="d-inline d-sm-none">I confirm info is true</span>
                                                                    </>
                                                                ) : (
                                                                    "I confirm the review is posted."
                                                                )}
                                                            </label>


                                                        </div>

                                                        <div className="mt-2 mt-md-0 ">
                                                            <Button
                                                                variant="success"
                                                                className="btn btn-sm d-flex align-items-center justify-content-center"
                                                                onClick={() => handleDoneStep('readBook', 'STEP3')}
                                                                disabled={!isSwitchChecked}
                                                            >
                                                                Next Step <FaArrowRight className="ms-2" />
                                                            </Button>
                                                        </div>
                                                    </div>

                                                </div>
                                            </>
                                        )}
                                    </div>

                                    {(book.selectedCurrentUserAssign?.assTypeId === 8)  && (
                                        <>
                                        <div className="row g-0 gap-2 w-100">
                                            <div className="col-12 col-md d-flex justify-content-start">
                                            <Button
                                                variant="success"
                                                className="d-flex btn btn-sm align-items-center"
                                                onClick={() => openRedirectedLink(`${String(user?.user?.audible_country)}/review/create-review/?ie=UTF8&channel=glance-detail&asin=${assignmentData.book_asin}`)}
                                            >
                                                Post Review on Audible<FaArrowRight className="ms-2"/>
                                            </Button>
                                        </div>
                                            <div className="col-12 col-md d-flex justify-content-md-end">

                                                {/* Кнопка "Next Step" - неактивна, если свитч выключен */}
                                                <Button
                                                    variant="success"
                                                    className="d-flex btn btn-sm align-items-center"
                                                    onClick={() => handleDoneStep('readBook', 'STEP3')}

                                                >
                                                    Next Step <FaArrowRight className="ms-2"/>
                                                </Button>

                                        </div>
                                        </div>
                                        </>
                                    )}

                                    </div>

                                </>
                            )}
                                </Card.Body>
                        </Card>
                        </Col>
                    </Row>
                    {![10, 11, 14, 15, 22, 23, 24, 25, 26, 27, 28, 29, 30, 31, 32].includes(book?.selectedCurrentUserAssign?.assTypeId) && (<>
                    {(currentStep === "STEP2" || currentStep === "STEP3" || currentStep === "STEP4") && (
                    <Row className="p-0 ps-2 pe-2 mt-0 mb-0 small">
                        <AIHelperProvider initialMode="writer">
                            <AIhelper
                                title={book.selectedCurrentUserAssign?.book?.title}
                                subtitle={book.selectedCurrentUserAssign?.book?.subtitle}
                                genre={currentGenreName}
                                description={book.selectedCurrentUserAssign?.book?.summary || book.selectedCurrentUserAssign?.book?.description_full || "No Description"}
                                author={book.selectedCurrentUserAssign?.book?.author}
                            />
                        </AIHelperProvider>
                    </Row>
                        )}
                            </>)}


                </>
            )}


            {/* Блок 3: Turn in this assignment – кнопки видны, если currentStep === "STEP3" */}
            {(currentStep === 'STEP2' || currentStep === 'STEP1' || currentStep === 'STEP3') && (
                <>
                    <Row className="p-0 ps-2 pe-2 mt-0 mb-2 small">
                        <Col md={12} className="mb-0 mt-0 p-0 ">
                            {/* Верхний ряд с текстом */}
                            <Card className="h-100 rounded p-1">
                                <Card.Body className="d-flex flex-column p-2 position-relative">
                            <div>

                                <h5 className="mx-auto text-muted mb-1"
                                    style={{ marginBottom: '-10px', whiteSpace: 'pre-line' }}>
                                    {assignmentData.full_title3}
                                </h5>

                                <p className="mb-2 text-muted">
                                    <strong> {assignmentData.full_subtitle3} </strong>
                                </p>
                                <p className="mb-0 text-muted">
                                {assignmentData.full_description3}
                                </p>
                            </div>

                            {(currentStep === "STEP3" ||
                                currentStep === "STEP4") && (
                                <div className="d-flex justify-content-end mt-0">

                                    <Button variant="success" className="btn btn-sm mx-1"

                                            onClick={() => setShowRatingModal(true)}


                                    >
                                        TURN IN THIS ASSIGNMENT
                                    </Button>

                                </div>
                            )}
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                    {![10, 11, 14, 15, 22, 23, 24, 25, 26, 27, 28, 29, 30, 31, 32].includes(book?.selectedCurrentUserAssign?.assTypeId) && (<>
                    {(currentStep === "STEP3") && (
                        <Row className="p-0 ps-2 pe-2 mt-0 mb-0 small">

                            <AIHelperProvider initialMode="writer">
                                <AIhelper
                                    title={book.selectedCurrentUserAssign?.book?.title}
                                    subtitle={book.selectedCurrentUserAssign?.book?.subtitle}
                                    genre={currentGenreName}
                                    description={book.selectedCurrentUserAssign?.book?.description_full || book.selectedCurrentUserAssign?.book?.summary ||  "No Description"}
                                    author={book.selectedCurrentUserAssign?.book?.author}
                                />
                            </AIHelperProvider>
                        </Row>
                    )}
                            </>)}

                </>
            )}


            {(currentStep === 'STEP4') && (
                <>
                    <Row className="p-0 ps-2 pe-2 mt-0 mb-2 small">
                        <Col md={12} className="mb-0 mt-0 p-0 ">
                            {/* Верхний ряд с текстом */}
                            <Card className="h-100 rounded p-1">
                                <Card.Body className="d-flex flex-column p-2 position-relative">
                            <div className="mb-0">
                                <h5 className="mx-auto text-muted mb-1">
                                    Assignment has been submitted and is now awaiting approval:
                                </h5>
                                <p className="mb-0 text-muted">
                                    <strong>You have successfully turned in this assignment.</strong> Our team will check it soon. If you have any questions, please contact us.
                                </p>
                                {book.selectedCurrentUserAssign?.assTypeId === 8 ? (
                                    <p className="mb-0 text-muted">
                                        <strong> To ensure we locate it, you can also provide the direct link to your reviewer page on Audible
                                            by clicking 'Submit Review Link' below.</strong>

                                    </p>
                                ) : ('')}
                            </div>

                            {book.selectedCurrentUserAssign?.assTypeId === 8 ? (
                                <>
                                <Row className="mt-3">
                                    <Col md={6}>

                                        <div>
                                            <h6>Provide the direct link to your Reviewer Page on Audible:</h6>
                                            <AudibleReviewParser onExtract={handleExtractAudibleReview} setCurrentStep={setCurrentStep}/>
                                        </div>
                                    </Col>
                                    <Col md={6}>
                                        {currentStep !== "STEP1" && (
                                            <div>
                                                <h6 className="text-muted mt-0">Instruction:</h6>
                                                <ListGroup variant="success" className="text-muted fst-italic">
                                                    <ListGroup.Item
                                                        className="text-muted"
                                                        style={{
                                                            paddingTop: '0.20rem', // Уменьшение высоты строки
                                                            paddingBottom: '0.20rem', // Уменьшение высоты строки
                                                            marginRight: '10px',
                                                        }}
                                                    >
                                                        Step 1: Find your Reviewer Page on Audible. This is the page where your name is displayed along with all your published reviews, from the most recent to the oldest.
                                                    </ListGroup.Item>
                                                    <ListGroup.Item
                                                        className="text-muted"
                                                        style={{
                                                            paddingTop: '0.20rem', // Уменьшение высоты строки
                                                            paddingBottom: '0.20rem', // Уменьшение высоты строки
                                                            marginRight: '10px',
                                                        }}
                                                    >
                                                        Step 2: Make sure that your review for this book is published and appears as the most recent one, meaning it should be the first in the list.
                                                    </ListGroup.Item>
                                                    <ListGroup.Item
                                                        className="text-muted"
                                                        style={{
                                                            paddingTop: '0.20rem', // Уменьшение высоты строки
                                                            paddingBottom: '0.20rem', // Уменьшение высоты строки
                                                            marginRight: '10px',
                                                        }}
                                                    >
                                                        Step 3: Copy URL of Reviewer Page and Paste it into the
                                                        input field.</ListGroup.Item>
                                                    <ListGroup.Item
                                                        className="text-muted"
                                                        style={{
                                                            paddingTop: '0.20rem', // Уменьшение высоты строки
                                                            paddingBottom: '0.20rem', // Уменьшение высоты строки
                                                            marginRight: '10px',
                                                        }}
                                                    >
                                                        Step 4: Click 'Submit Review Link' to speed up
                                                        approval!</ListGroup.Item>
                                                </ListGroup>
                                            </div>
                                        )}
                                    </Col>
                                </Row>
                                </>
                            ) : (
                                <>
                                {!isProofRequiredAssignment(book?.selectedCurrentUserAssign?.assTypeId) && (<>
                                   <Row className="mt-3">
                                        <Col lg={12} className="border-end">
                                            <div className="mb-0">
                                            <h6 className="mb-2 mt-0 text-muted">
                                                <span>Provide the link to your review to speed up the approval:</span>
                                            </h6>
                                                <ReviewSubmiterUserSide
                                                    assignmentData={book?.selectedCurrentUserAssign}
                                                    assId={assId}
                                                    onSaved={(msg) => {
                                                        setServerMessage(msg);
                                                        refetchThisAssignment();
                                                    }}
                                                />
                                            </div>
                                        </Col>

                                    </Row>
                                        </>)}
                                </>
                            )}



                                   <Row className="mt-0">
                                        <Col md={12} className=" mb-1 py-3 px-3  rounded">
                                            <div>

                                                <h5 className="mx-auto text-muted mb-1">
                                                    {assignmentData.full_title4}
                                                </h5>

                                                <p className="mb-0 text-muted">
                                                    <strong> {assignmentData.full_subtitle4} </strong>
                                                </p>
                                                <p className="mb-0 text-muted">
                                                    {assignmentData.full_description4}
                                                </p>

                                                    {/* SCROLLABLE CONTAINER */}
                                                {!isProofRequiredAssignment(book?.selectedCurrentUserAssign?.assTypeId) && (<>
                                                {/* ============== MOBILE (xs) — карточки ============== */}
                                                <div className="d-sm-none">
                                                    {(() => {
                                                        const Stars = ({ value = 0 }) => (
                                                            <>
                                                                {[...Array(5)].map((_, i) => (
                                                                    <i
                                                                        key={i}
                                                                        className={`bi bi-star${i < Number(value) ? "-fill" : ""}`}
                                                                        style={{ color: i < Number(value) ? "gold" : "lightgray", fontSize: 12, marginRight: 4 }}
                                                                    />
                                                                ))}
                                                            </>
                                                        );

                                                        const ass = book?.selectedCurrentUserAssign ?? {};
                                                        const showMismatchCol =
                                                            !!ass?.checked_review_name ||
                                                            !!ass?.checked_review_rating ||
                                                            !!ass?.checked_asin ||
                                                            !!ass?.checked_review_status ||
                                                            !!ass?.checked_review_photo;

                                                        const RowCard = ({ title, declared, result, mismatch }) => (
                                                            <div className="border rounded-2 p-2 mb-2 bg-light">
                                                                <div className="mb-1 text-success fw-bold">{title}</div>
                                                                <div className="d-flex flex-column gap-1">
                                                                    <div><span className="fw-semibold">You Declared:</span> {declared}</div>
                                                                    <div><span className="fw-semibold">Verification:</span> {result}</div>
                                                                    {showMismatchCol && mismatch != null && (
                                                                        <div><span className="fw-semibold">Match:</span> {mismatch}</div>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        );

                                                        return (
                                                            <>
                                                                {/* Rating */}
                                                                <RowCard
                                                                    title={`${ass?.assTypeId === 8 ? "Overall " : ""}Rating`}
                                                                    declared={<Stars value={ass?.rating} />}
                                                                    result={
                                                                        ass?.checked_review_rating != null
                                                                            ? <Stars value={ass?.checked_review_rating} />
                                                                            : <span className="text-success fw-bold">Awaiting the submission of the review link...</span>
                                                                    }
                                                                    mismatch={
                                                                        ass?.checked_review_rating != null
                                                                            ? (parseInt(String(ass?.checked_review_rating || "").trim(), 10) ===
                                                                            parseInt(String(ass?.rating || "").trim(), 10)
                                                                                ? "✅ Matched"
                                                                                : "❌ Not Matched")
                                                                            : null
                                                                    }
                                                                />

                                                                {/* Reviewer Name */}
                                                                <RowCard
                                                                    title="Reviewer Name"
                                                                    declared={assignmentData?.review_name}
                                                                    result={
                                                                        ass?.checked_review_name
                                                                            ? ass?.checked_review_name
                                                                            : <span className="text-success fw-bold">Awaiting the submission of the review link...</span>
                                                                    }
                                                                    mismatch={
                                                                        ass?.checked_review_name
                                                                            ? (reviewerNamesMatch(ass?.checked_review_name, assignmentData?.review_name) ? "✅ Matched" : "❌ Not Matched")
                                                                            : null
                                                                    }
                                                                />

                                                                {/* ASIN */}
                                                                <RowCard
                                                                    title="ASIN"
                                                                    declared={assignmentData?.book_asin}
                                                                    result={
                                                                        ass?.checked_asin
                                                                            ? ass?.checked_asin
                                                                            : <span className="text-success fw-bold">Awaiting the submission of the review link...</span>
                                                                    }
                                                                    mismatch={
                                                                        ass?.checked_asin
                                                                            ? (() => {
                                                                                const checked = ass?.checked_asin;
                                                                                const mainAsin = assignmentData?.book_asin;
                                                                                const audio = ass?.book?.audio_asin;
                                                                                const kindle = ass?.book?.kindle_asin;
                                                                                const paperback = ass?.book?.paper_back_asin;
                                                                                const Hardcover = ass?.book?.hard_cover_asin;

                                                                                if (checked === mainAsin) return "✅ Matched";
                                                                                const belongs = checked === audio || checked === kindle || checked === paperback || checked === Hardcover;
                                                                                if (belongs) return "✅ Not Matched, but belongs to Book";
                                                                                if (isStaffApprovedFinalAssignment(ass)) return "Approved by Staff";
                                                                                return "❌ Not Matched at All";
                                                                            })()
                                                                            : null
                                                                    }
                                                                />

                                                                {/* Verified Purchase */}
                                                                {[3, 4, 5, 6, 9, 12, 13, 16, 17, 18, 19, 20, 21, 24, 25, 26, 27].includes(ass?.assTypeId) && (
                                                                    <RowCard
                                                                        title="Verified Purchase"
                                                                        declared={assignmentData?.verified_purchase}
                                                                        result={
                                                                            ass?.checked_review_status
                                                                                ? ass?.checked_review_status
                                                                                : <span className="text-success fw-bold">Awaiting the submission of the review link...</span>
                                                                        }
                                                                        mismatch={
                                                                            ass?.checked_review_status
                                                                                ? (ass?.checked_review_status === assignmentData?.verified_purchase ? "✅ Matched" : "❌ Not Matched")
                                                                                : null
                                                                        }
                                                                    />
                                                                )}

                                                                {/* Media in Review */}
                                                                {[4, 6, 12, 13, 16, 17, 18, 25, 26].includes(ass?.assTypeId) && (
                                                                    <RowCard
                                                                        title="Media in Review"
                                                                        declared={assignmentData?.photo}
                                                                        result={
                                                                            ass?.checked_asin
                                                                                ? (ass?.checked_review_photo ? ass?.checked_review_photo : "No Media")
                                                                                : <span className="text-success fw-bold">Awaiting the submission of the review link...</span>
                                                                        }
                                                                        mismatch={
                                                                            ass?.checked_review_status
                                                                                ? (ass?.checked_review_photo !== "" ? "✅ Matched" : "❌ Not Matched")
                                                                                : null
                                                                        }
                                                                    />
                                                                )}
                                                            </>
                                                        );
                                                    })()}
                                                </div>


                                                <div className="table-responsive mb-0" style={{ overflowX: 'auto', paddingBottom: '8px' }}>
                                                    {/* ============== DESKTOP (≥sm) — твоя таблица ============== */}
                                                    <div className="d-none d-sm-block">
                                                    <table className="min-w-full border border-gray-300 shadow-md rounded-lg mx-auto" style={{lineHeight: "16px", width: "90%" }}> {/* ✅ Центрирование */}
                                                        <thead>
                                                        <tr className="bg-secondary bg-opacity-50 text-sm">

                                                            <th className="px-2 py-1 border">Field:</th>
                                                            <th className="px-2 py-1 border">You Declared:</th>
                                                            <th className="px-2 py-1 border">Verification Result:</th>
                                                            {book.selectedCurrentUserAssign?.checked_review_name ? (
                                                            <th className="px-2 py-1 border">Mismatches:</th>
                                                                ):('')}
                                                        </tr>
                                                        </thead>
                                                        <tbody>
                                                        <tr className=" text-sm">
                                                            <td className="px-2 py-1 border font-semibold">{book.selectedCurrentUserAssign?.assTypeId === 8 && "Overall"} Rating:</td>
                                                            <td className="px-2 py-1 border">
                                                                {[...Array(5)].map((_, index) => (
                                                                    <i
                                                                        key={index}
                                                                        className={`bi bi-star${index < book.selectedCurrentUserAssign?.rating ? '-fill' : ''}`}
                                                                        style={{
                                                                            color: index < book.selectedCurrentUserAssign?.rating ? 'gold' : 'lightgray',
                                                                            fontSize: '12px',
                                                                            marginRight: '5px'
                                                                        }}
                                                                    ></i>
                                                                ))}

                                                                </td>
                                                            <td className="px-2 py-1 border">
                                                                {book.selectedCurrentUserAssign?.checked_review_rating ? (
                                                                    <>
                                                                    {[...Array(5)].map((_, index) => (
                                                                    <i
                                                                    key={index}
                                                                className={`bi bi-star${index < book.selectedCurrentUserAssign?.checked_review_rating ? '-fill' : ''}`}
                                                                style={{
                                                                    color: index < book.selectedCurrentUserAssign?.checked_review_rating ? 'gold' : 'lightgray',
                                                                    fontSize: '12px',
                                                                    marginRight: '5px'
                                                                }}
                                                            ></i>
                                                            ))}

                                                                    </>
                                                                    ):
                                                                    (  <span className="text-green-600 font-bold">Awaiting the submission of the review link...</span>)}

                                                            </td>


                                                                {book.selectedCurrentUserAssign?.checked_review_rating ? (
                                                                    <>
                                                                    <td className="px-2 py-1 border">
                                                                        {(parseInt(String(book?.selectedCurrentUserAssign?.checked_review_rating || '').trim(), 10)
                                                                    === parseInt(String(book?.selectedCurrentUserAssign?.rating || '').trim(), 10))
                                                                    ? '✅ Matched'
                                                                    : '❌ Not Matched'}
                                                                    </td>
                                                                   </>
                                                                ):('')}


                                                        </tr>
                                                        <tr className="bg-secondary bg-opacity-10 text-sm">
                                                            <td className="px-2 py-1 border font-semibold">Reviewer Name:</td>
                                                            <td className="px-2 py-1 border">{assignmentData?.review_name}</td>
                                                            <td className="px-2 py-1 border">
                                                                {book.selectedCurrentUserAssign?.checked_review_name ? (
                                                                        <>{book.selectedCurrentUserAssign?.checked_review_name}
                                                                       </>
                                                                ):(  <span className="text-green-600 font-bold">Awaiting the submission of the review link...</span>)
                                                                }
                                                            </td>
                                                            {book.selectedCurrentUserAssign?.checked_review_name ? (
                                                            <td className="px-2 py-1 border">
                                                                {reviewerNamesMatch(book.selectedCurrentUserAssign?.checked_review_name, assignmentData.review_name) ? ('✅ Matched') : ('❌ Not Matched')}
                                                            </td>
                                                                ):('')}
                                                        </tr>
                                                        <tr className=" text-sm">

                                                            <td className="px-2 py-1 border font-semibold">ASIN:</td>
                                                            <td className="px-2 py-1 border">{assignmentData.book_asin}</td>
                                                            <td className="px-2 py-1 border">
                                                                {book.selectedCurrentUserAssign?.checked_asin ? (
                                                                    <>{book.selectedCurrentUserAssign?.checked_asin}
                                                                    </>
                                                                ):(  <span className="text-green-600 font-bold">Awaiting the submission of the review link...</span>)
                                                                }
                                                            </td>
                                                            {book.selectedCurrentUserAssign?.checked_asin ? (
                                                            <td className="px-2 py-1 border">
                                                                {(() => {
                                                                    const checkedAsin = book.selectedCurrentUserAssign?.checked_asin;
                                                                    const mainAsin = assignmentData?.book_asin;

                                                                    const audio = book.selectedCurrentUserAssign?.book?.audio_asin;
                                                                    const kindle = book.selectedCurrentUserAssign?.book?.kindle_asin;
                                                                    const paperback = book.selectedCurrentUserAssign?.book?.paper_back_asin;
                                                                    const Hardcover = book.selectedCurrentUserAssign?.book?.hard_cover_asin;

                                                                    if (checkedAsin === mainAsin) {
                                                                        return '✅ Matched';
                                                                    }

                                                                    const belongsToBook =
                                                                        checkedAsin === audio ||
                                                                        checkedAsin === kindle ||
                                                                        checkedAsin === paperback ||
                                                                        checkedAsin === Hardcover;

                                                                    if (belongsToBook) {
                                                                        return '✅ Not Matched, but belongs to Book';
                                                                    }

                                                                    if (isStaffApprovedFinalAssignment(book.selectedCurrentUserAssign)) {
                                                                        return 'Approved by Staff';
                                                                    }

                                                                    return '❌ Not Matched at All';
                                                                })()}
                                                            </td>
                                                                ):('')}
                                                        </tr>
                                                        {[3, 4, 5, 6, 9, 12, 13, 16, 17, 18, 19, 20, 21, 24, 25, 26, 27].includes(book.selectedCurrentUserAssign?.assTypeId) && (
                                                            <tr className="bg-secondary bg-opacity-10 text-sm">
                                                            <td className="px-2 py-1 border font-semibold">Verified Purchase:</td>

                                                            <td className="px-2 py-1 border">{assignmentData.verified_purchase}</td>
                                                            <td className="px-2 py-1 border">
                                                                {book.selectedCurrentUserAssign?.checked_review_status ? (book.selectedCurrentUserAssign?.checked_review_status):( <span className="text-green-600 font-bold">Awaiting the submission of the review link...</span>)}

                                                            </td>
                                                                {book.selectedCurrentUserAssign?.checked_review_status ? (
                                                                    <td className="px-2 py-1 border">
                                                                        {book.selectedCurrentUserAssign?.checked_review_status === assignmentData.verified_purchase ? ('✅ Matched') : ('❌ Not Matched')}
                                                                    </td>
                                                                ):('')}
                                                        </tr>
                                                    )}
                                                        {[4, 6, 12, 13, 16, 17, 25, 26].includes(book.selectedCurrentUserAssign?.assTypeId) && (

                                                            <tr className=" text-sm">
                                                            <td className="px-2 py-1 border font-semibold">Media in Review:</td>
                                                            <td className="px-2 py-1 border">{assignmentData.photo}</td>
                                                                <td className="px-2 py-1 border" style={{
                                                                    whiteSpace: "nowrap",
                                                                    overflow: "hidden",
                                                                    textOverflow: "ellipsis",
                                                                    maxWidth: "400px",  // Ограничиваем ширину
                                                                    width: "400px"      // Фиксируем ширину
                                                                }}>
                                                                    {book.selectedCurrentUserAssign?.checked_review_photo ? (
                                                                        book.selectedCurrentUserAssign?.checked_review_photo
                                                                    ) : (
                                                                        book.selectedCurrentUserAssign?.checked_asin ? (
                                                                            "No Media"
                                                                        ) : (
                                                                            <span className="text-green-600 font-bold">Awaiting the submission of the review link...</span>
                                                                        )
                                                                    )}
                                                                </td>

                                                                {book.selectedCurrentUserAssign?.checked_review_status ? (
                                                                    <td className="px-2 py-1 border">
                                                                        {book.selectedCurrentUserAssign?.checked_review_photo !== '' ? ('✅ Matched') : ('❌ Not Matched')}
                                                                    </td>
                                                                ):('')}
                                                        </tr>
                                                        )}
                                                        </tbody>
                                                    </table>
                                                    </div>
                                                </div>
                                               </>)}

                                                {[10, 11, 14, 15, 22, 23, 24, 27, 18, 19, 20, 21].includes(book.selectedCurrentUserAssign?.assTypeId) && (<>
                                                    {/* Две фото из БД */}
                                                    {(photo1 || photo2) && (
                                                        <>
                                                            <h6 className="text-muted mt-3 me-2">Your uploaded purchase confirmation files:</h6>
                                                            <div
                                                                className="mt-2 mb-2"
                                                                style={{
                                                                    display: "grid",
                                                                    gridTemplateColumns: proofUrls.length > 1 ? "repeat(2, minmax(0, 1fr))" : "minmax(0, 50%)",
                                                                    justifyContent: "start",
                                                                    gap: 12,
                                                                    width: "100%",
                                                                }}
                                                            >
                                                                {proofUrls.map((url, idx) => (
                                                                    <ProofImageCard
                                                                        key={`proof-${idx}`}
                                                                        src={url}
                                                                        alt={`Proof ${idx + 1}`}
                                                                        fallbackSrc={proofFallback}
                                                                    />
                                                                ))}
                                                            </div>
                                                        </>
                                                    )}
                                                </>)}
                                                {[28, 29, 30, 31, 32].includes(book.selectedCurrentUserAssign?.assTypeId) && (<>
                                                    {/* Две фото из БД */}
                                                    {photo1 && (
                                                        <>
                                                            <h6 className="text-muted mt-3 me-2">Your uploaded confirmation file:</h6>
                                                            <div
                                                                className="mt-2 mb-2"
                                                                style={{
                                                                    display: "grid",
                                                                    gridTemplateColumns: proofUrls.length > 1 ? "repeat(2, minmax(0, 1fr))" : "minmax(0, 50%)",
                                                                    justifyContent: "start",
                                                                    gap: 12,
                                                                    width: "100%",
                                                                }}
                                                            >
                                                                {proofUrls.map((url, idx) => (
                                                                    <ProofImageCard
                                                                        key={`proof-${idx}`}
                                                                        src={url}
                                                                        alt={`Proof ${idx + 1}`}
                                                                        fallbackSrc={proofFallback}
                                                                    />
                                                                ))}
                                                            </div>
                                                        </>
                                                    )}
                                                </>)}
                                            </div>


                                        </Col>
                                    </Row>
                                </Card.Body>
                            </Card>

                        </Col>

                    </Row>

                </>
            )}

            {/* Блок 5: STEP5 */}
            {(currentStep === 'STEP5') && (
                <>
                    <Row className="p-0 ps-2 pe-2 mt-0 mb-2 small">
                        <Col md={12} className="mb-0 mt-0 p-0 ">
                            <Card className=" rounded p-1">
                                <Card.Body className="p-2 " >
                                    <p className="mb-0 mt-0 text-muted">
                                    {(() => {
                                        const r = book.selectedCurrentUserAssign;
                                        const cs = String(r?.check_status || "").toUpperCase();

                                        // Complaint approved (bad for reviewer)
                                        if (cs === "REFUNDED") {
                                            return (
                                                <Row className="d-flex justify-content-center mb-3 mt-3">
                                                    <Col md={11} className="p-0 text-start">
                                                        <div className="card border-2 border-danger shadow-sm rounded" aria-live="polite">
                                                            <div className="card-body p-3">
                                                                <h6 className="fs-6 mb-1 text-danger d-flex align-items-center">
                                                                    <i className="bi bi-exclamation-octagon-fill me-2"></i>
                                                                    Review Flagged by Publisher - Complaint Approved
                                                                </h6>
                                                                <div className="mb-2">
                                                                    A complaint was filed against your review and has been approved by support. The assignment was reversed. Deducted: -{book.selectedCurrentUserAssign?.assignment_price_reader} Virt
                                                                    {book.selectedCurrentUserAssign?.received_bonus > 0 && ` - ${book.selectedCurrentUserAssign?.received_bonus} Virt`}

                                                                </div>
                                                                {r?.complaint_flow?.trim() && (
                                                                    <div className="text-muted">
                                                                        <span className="fw-semibold">Details:</span> {r.complaint_flow}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </Col>
                                                </Row>
                                            );
                                        }

                                        // Complaint in progress (under review)
                                        if (cs === "COMPLAINT") {
                                            return (
                                                <Row className="d-flex justify-content-center mb-3 mt-3">
                                                    <Col md={11} className="p-0 text-start">
                                                        <div className="card border-2 border-danger shadow-sm rounded" aria-live="polite">
                                                            <div className="card-body p-3">
                                                                <h6 className="fs-6 mb-1 text-danger d-flex align-items-center">
                                                                    <i className="bi bi-hourglass-split me-2"></i>
                                                                    Review Flagged by Publisher - Complaint In Progress
                                                                </h6>
                                                                <div className="mb-2">
                                                                    A complaint has been filed against your review and is currently under review. We’ll notify you once a decision is made.
                                                                </div>
                                                                {r?.complaint_flow?.trim() && (
                                                                    <div className="text-muted">
                                                                        <span className="fw-semibold">Details:</span> {r.complaint_flow}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </Col>
                                                </Row>
                                            );
                                        }

                                        // Complaint declined (good for reviewer)
                                        if (cs === "DECLINED") {
                                            return (
                                                <Row className="d-flex justify-content-center mb-3 mt-3">
                                                    <Col md={11} className="p-0 text-start">
                                                        <div className="card border-2 border-success shadow-sm rounded" aria-live="polite">
                                                            <div className="card-body p-3">
                                                                <h6 className="fs-6 mb-1 text-success d-flex align-items-center">
                                                                    <i className="bi bi-check-circle-fill me-2"></i>
                                                                    Review Flagged by Publisher - No Violation Found
                                                                </h6>
                                                                <div className="mb-2">
                                                                    The complaint against your review was declined. No action is required.
                                                                </div>
                                                                {r?.complaint_flow?.trim() && (
                                                                    <div className="text-muted">
                                                                        <span className="fw-semibold">Reason:</span> {r.complaint_flow}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </Col>
                                                </Row>
                                            );
                                        }

                                        return null;
                                    })()}
                                </p>

                                    <h5 className="text-muted mb-1" >
                                            {assignmentData.full_title5}
                                        </h5>
                                        <p className="mb-0 text-muted">
                                            <strong>{assignmentData.full_subtitle5}</strong>
                                        </p>
                                        <p className="mb-0 text-muted">{assignmentData.full_description5}</p>
                                    {!isProofRequiredAssignment(book.selectedCurrentUserAssign?.assTypeId) && (<>
                                    {/* SCROLLABLE CONTAINER */}
                                    <div className="table-responsive mb-0" style={{ overflowX: 'auto', paddingBottom: '8px' }}>

                                            <table className="min-w-full border border-gray-300 shadow-md rounded-lg mx-auto" style={{lineHeight: "16px", width: "90%" }}> {/* ✅ Центрирование */}
                                                <thead>
                                                <tr className="bg-secondary bg-opacity-50 text-sm">
                                                    <th className="px-2 py-1 border" style={{ whiteSpace: "nowrap" }}>Field</th>
                                                    <th className="px-2 py-1 border" style={{ whiteSpace: "nowrap" }}>You Declared</th>
                                                    <th className="px-2 py-1 border" style={{ whiteSpace: "nowrap" }}>Verification Result</th>
                                                    <th className="px-2 py-1 border" style={{ whiteSpace: "nowrap" }}>Matched?</th>
                                            </tr>
                                            </thead>
                                            <tbody>
                                            <tr>
                                                <td className="px-2 py-1 fw-semibold">{book.selectedCurrentUserAssign?.assTypeId === 8 && 'Overall'} Rating</td>
                                                <td className="px-2 py-1" style={{ whiteSpace: "nowrap" }}>
                                                    {[...Array(5)].map((_, index) => (
                                                        <i
                                                            key={index}
                                                            className={`bi bi-star${index < book.selectedCurrentUserAssign?.rating ? '-fill' : ''}`}
                                                            style={{ color: index < book.selectedCurrentUserAssign?.rating ? 'gold' : 'lightgray', fontSize: '14px', marginRight: '3px' }}
                                                        />
                                                    ))}
                                                </td>
                                                <td className="px-2 py-1" style={{ whiteSpace: "nowrap" }}>
                                                    {[...Array(5)].map((_, index) => (
                                                        <i
                                                            key={index}
                                                            className={`bi bi-star${index < book.selectedCurrentUserAssign?.checked_review_rating ? '-fill' : ''}`}
                                                            style={{ color: index < book.selectedCurrentUserAssign?.checked_review_rating ? 'gold' : 'lightgray', fontSize: '14px', marginRight: '3px' }}
                                                        />
                                                    ))}
                                                </td>
                                                <td className="px-2 py-1 text-success fw-bold">
                                                   {(parseInt(String(book?.selectedCurrentUserAssign?.checked_review_rating || '').trim(), 10)
                                                            === parseInt(String(book?.selectedCurrentUserAssign?.rating || '').trim(), 10))
                                                            ? '✅ Matched'
                                                            : '❌ Not Matched'}
                                                     </td>


                                            </tr>
                                            <tr className="bg-secondary bg-opacity-10">
                                                <td className="px-2 py-1 fw-semibold">Reviewer</td>
                                                <td className="px-2 py-1" style={{ whiteSpace: "nowrap" }}>{book.selectedCurrentUserAssign?.reviewer_name}</td>
                                                <td className="px-2 py-1 text-success fw-bold" style={{ whiteSpace: "nowrap" }}>{book.selectedCurrentUserAssign?.checked_review_name} {!reviewerNamesMatch(book.selectedCurrentUserAssign?.checked_review_name, book.selectedCurrentUserAssign?.reviewer_name) && (
                                                        <>changed along the way, current: {user?.user?.amz_name}</>
                                                    )}

                                                </td>
                                                <td className="px-2 py-1 text-success fw-bold" style={{ whiteSpace: "nowrap" }}>✅ Matched</td>
                                            </tr>

                                            <tr>
                                                <td className="px-2 py-1 fw-semibold" style={{ whiteSpace: "nowrap" }}>ASIN</td>
                                                <td className="px-2 py-1" style={{ whiteSpace: "nowrap" }}>{assignmentData.book_asin}</td>
                                                <td className="px-2 py-1 text-success fw-bold" style={{ whiteSpace: "nowrap" }}>{book.selectedCurrentUserAssign?.checked_asin}</td>
                                                <td className="px-2 py-1" style={{ whiteSpace: "nowrap" }}>
                                                    {(() => {
                                                        const checkedAsin = book.selectedCurrentUserAssign?.checked_asin;
                                                        const mainAsin = assignmentData?.book_asin;
                                                        const audio = book.selectedCurrentUserAssign?.book?.audio_asin;
                                                        const kindle = book.selectedCurrentUserAssign?.book?.kindle_asin;
                                                        const paperback = book.selectedCurrentUserAssign?.book?.paper_back_asin;
                                                        const Hardcover = book.selectedCurrentUserAssign?.book?.hard_cover_asin;

                                                        if (checkedAsin === mainAsin) {
                                                            return <span className="text-success fw-bold">✅ Matched</span>;
                                                        }

                                                        const belongsToBook = [audio, kindle, paperback, Hardcover].includes(checkedAsin);

                                                        if (belongsToBook) {
                                                            return <span className="text-secondary fw-bold">✅ Not Matched, but this book</span>;
                                                        }

                                                        if (isStaffApprovedFinalAssignment(book.selectedCurrentUserAssign)) {
                                                            return <span className="text-success fw-bold">Approved by Staff</span>;
                                                        }

                                                        return <span className="text-danger fw-bold">❌ Not Matched (unrelated)</span>;
                                                    })()}
                                                </td>
                                            </tr>

                                            {[3, 4, 5, 6, 9, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 24, 25, 26, 27].includes(book.selectedCurrentUserAssign?.assTypeId) && (
                                                <tr className="bg-secondary bg-opacity-10" style={{ whiteSpace: "nowrap" }}>
                                                    <td className="px-2 py-1 fw-semibold">Verified Purchase</td>
                                                    <td className="px-2 py-1">{assignmentData.verified_purchase}</td>
                                                    <td className="px-2 py-1 text-success fw-bold">{book.selectedCurrentUserAssign?.checked_review_status || 'Not Verified Purchase'}</td>
                                                    <td className="px-2 py-1">
                                                       <span className="text-success fw-bold"> {book.selectedCurrentUserAssign?.checked_review_status === assignmentData.verified_purchase ? '✅ Matched' : '❌ Not Matched'}</span>
                                                    </td>
                                                </tr>
                                            )}

                                            {[4, 6, 12, 13, 16, 17, 25, 26].includes(book.selectedCurrentUserAssign?.assTypeId) && (
                                                <tr>
                                                    <td className="px-2 py-1 fw-semibold">Media in Review</td>
                                                    <td className="px-2 py-1">{assignmentData.photo}</td>
                                                    <td className="px-2 py-1 text-success fw-bold">{book.selectedCurrentUserAssign?.checked_review_photo ? 'This Amazon review contains media' : 'No media attached'}</td>
                                                    <td className="px-2 py-1">
                                                        <span className="text-success fw-bold">  {book.selectedCurrentUserAssign?.checked_review_photo ? '✅ Matched' : '❌ Not Matched'} </span>
                                                    </td>
                                                </tr>
                                            )}
                                            </tbody>
                                        </table>
                                    </div>
                                    <Row className="d-flex justify-content-center ps-3 pe-3 mt-3">
                                        <Col md={11} className="p-0 shadow-sm rounded text-start">
                                            <Card className="border-secondary">
                                                <Card.Header className="py-1">After Review check we found such data:</Card.Header>
                                                <Card.Body className="p-3">
                                                    <div className="table-responsive mb-0" style={{ overflowX: 'auto', paddingBottom: '8px' }}>
                                                    <table className="min-w-full border border-gray-300 shadow-md rounded-lg mx-auto" style={{lineHeight: "16px",  }}>
                                                        <thead>
                                                        <tr className="bg-secondary bg-opacity-50 text-sm">
                                                            <th className="px-2 py-1 border fw-semibold" style={{ whiteSpace: "nowrap" }}>Reviewer:</th>
                                                            <th className="px-2 py-1 border fw-semibold" style={{ whiteSpace: "nowrap" }}>Date:</th>
                                                            <th className="px-2 py-1 border fw-semibold" style={{ whiteSpace: "nowrap" }}>Country:</th>
                                                            <th className="px-2 py-1 border fw-semibold" style={{ whiteSpace: "nowrap" }}>ASIN:</th>
                                                            <th className="px-2 py-1 border fw-semibold" style={{ whiteSpace: "nowrap" }}>Type:</th>
                                                            {a?.amz_review_id && (
                                                            <th className="px-2 py-1 border fw-semibold" style={{ whiteSpace: "nowrap" }}>Amz Rev ID:</th>
                                                                )}
                                                            <th className="px-2 py-1 border fw-semibold" style={{ whiteSpace: "nowrap" }}>Check Date:</th>

                                                        </tr>
                                                        </thead>
                                                        <tbody>
                                                        <tr>
                                                            <td className="px-2 py-1 border font-semibold" style={{ whiteSpace: "nowrap" }}>{a?.checked_review_name || "No Name"}</td>
                                                            <td className="px-2 py-1 border font-semibold" style={{ whiteSpace: "nowrap" }}>{a?.checked_review_date || "No Date"}</td>
                                                            <td className="px-2 py-1 border font-semibold" style={{ whiteSpace: "nowrap" }}>{a?.checked_review_country || "No Country"}</td>
                                                            <td className="px-2 py-1 border font-semibold" style={{ whiteSpace: "nowrap" }}>{a?.checked_asin || "No ASIN"}</td>
                                                            <td className="px-2 py-1 border font-semibold" style={{ whiteSpace: "nowrap" }}>{a?.assTypeId === 8 ? ("Audible Review"):(a?.checked_review_status || "Unverified Purchase")}</td>
                                                            {a?.amz_review_id && (
                                                            <td className="px-2 py-1 border font-semibold" style={{ whiteSpace: "nowrap" }}>{a?.amz_review_id ? a?.amz_review_id : "No ARID"}</td>
                                                            )}
                                                                <td
                                                                className="px-2 py-1 border font-semibold"
                                                                style={{ whiteSpace: "nowrap" }}
                                                            >
                                                                {a?.end_rev_date
                                                                    ? new Date(a.end_rev_date*60*1000).toLocaleDateString("en-GB", {
                                                                        month: "long",
                                                                        day: "2-digit",
                                                                        year: "numeric",
                                                                    })
                                                                    : "No Date"}
                                                            </td>
                                                        </tr>
                                                        </tbody>
                                                    </table>
                                                    </div>
                                                    <Row className=" mt-2">
                                                        <Col>
                                                            <strong className="text-muted mt-2">Title of Review:</strong>
                                                            <p className="me-2 mb-1 mt-0 fst-italic text-muted" style={{ lineHeight: "1.2" }}>
                                                                {a?.checked_review_title || "No Title"}</p>
                                                        </Col>
                                                    </Row>




                                                    <strong className="text-muted">Text of Review: </strong>
                                                    <p className="text-dark text-muted fst-italic small" style={{ lineHeight: "1.2" }}>{a?.checked_review_text || "No Text"}
                                                        <br />
                                                    </p>
                                                    {a?.checked_review_photo && (
                                                        <>
                                                            {(() => {
                                                                const isVideoUrl = (u = "") =>
                                                                    /\.mp4(\?|$)/i.test(u) || u.includes("/vse-vms-transcoding-artifact-");

                                                                const PHOTO_ONLY_TYPES = new Set([18, 19, 20, 21]);
                                                                const assType = Number(a?.assTypeId ?? book?.selectedCurrentUserAssign?.assTypeId);
                                                                const imgWidth = PHOTO_ONLY_TYPES.has(assType) ? 420 : 115;

                                                                let urls;
                                                                if (PHOTO_ONLY_TYPES.has(assType)) {
                                                                    const photo1 = buildPhotoUrl(a?.checked_review_photo ?? book?.selectedCurrentUserAssign?.checked_review_photo);
                                                                    const photo2 = buildPhotoUrl(a?.checked_review_photo2 ?? book?.selectedCurrentUserAssign?.checked_review_photo2);
                                                                    urls = [photo1, photo2].filter(Boolean);
                                                                } else {
                                                                    urls = [
                                                                        a?.checked_review_photo,
                                                                        a?.checked_review_photo2,
                                                                        a?.checked_review_photo3,
                                                                        a?.checked_review_photo4,
                                                                        a?.checked_review_photo5,
                                                                    ].filter(Boolean).slice(0, 5);
                                                                }

                                                                if (!urls.length) return null;

                                                                    const fallback = `${process.env.REACT_APP_API_URL}covers/empty-cover.jpg`;

                                                                return (<>
                                                                    <strong className="text-muted mt-0 me-2">
                                                                        {PHOTO_ONLY_TYPES.has(assType)
                                                                            ? "Receipt and photos provided by the reviewer:"
                                                                            : "Media in Review:"}
                                                                    </strong>
                                                                    <div
                                                                        className="d-flex align-items-center justify-content-start flex-nowrap overflow-auto mt-2 mb-2"
                                                                        style={{ gap: 8 }}
                                                                    >
                                                                        {urls.map((url, idx) =>
                                                                            isVideoUrl(url) ? (
                                                                                <video
                                                                                    key={`media-${idx}`}
                                                                                    src={url}
                                                                                    preload="metadata"
                                                                                    controls
                                                                                    className="shadow rounded"
                                                                                    style={{ width: 200, borderRadius: 6, objectFit: "cover" }}
                                                                                    onError={(e) => {
                                                                                        e.currentTarget.outerHTML = `
                  <img 
                    src="${fallback}" 
                    style="width:120px; height:120px; object-fit:cover; border-radius:6px" 
                    class="shadow"
                    alt="Fallback"
                  />
                `;
                                                                                    }}
                                                                                />
                                                                            ) : (
                                                                                <Image
                                                                                    key={`media-${idx}`}
                                                                                    src={url}
                                                                                    alt={`Review Media ${idx + 1}`}
                                                                                    className="border border-secondary"
                                                                                    style={{ width: imgWidth, objectFit: "cover", borderRadius: 6, cursor: "zoom-in" }}
                                                                                    onError={(e) => {
                                                                                        e.currentTarget.onerror = null;
                                                                                        e.currentTarget.src = fallback;
                                                                                    }}
                                                                                    onClick={() => setFullImage(url)}
                                                                                />
                                                                            )
                                                                        )}
                                                                    </div>
                                                                        </>
                                                                );
                                                            })()}
                                                        </>
                                                    )}
                                                    <strong className="text-muted mb-0">Review Link: </strong>
                                                    <p className="mt-0 mb-0 text-muted" style={{ lineHeight: "1.2" }}>
                                                        {/* Desktop / Tablet */}
                                                        <span
                                                            className="text-primary small text-break d-none d-md-inline"
                                                            role="button"
                                                            onClick={async () => {
                                                                try {
                                                                    const targetUrl = a?.review_link;
                                                                    if (!targetUrl) return;

                                                                    const response = await getRedirectUrlApi(targetUrl);

                                                                    if (response && response.redirectUrl) {
                                                                        window.open(response.redirectUrl, "_blank", "noopener,noreferrer");
                                                                    } else {
                                                                        console.error("Failed to get redirect URL");
                                                                    }
                                                                } catch (err) {
                                                                    console.error("Redirect error:", err);
                                                                }
                                                            }}
                                                        >
    <strong>{a?.review_link}</strong>
  </span>

                                                        {/* Mobile */}
                                                        <span
                                                            className="text-primary small d-inline d-md-none"
                                                            role="button"
                                                            onClick={async () => {
                                                                try {
                                                                    const targetUrl = a?.review_link;
                                                                    if (!targetUrl) return;

                                                                    const response = await getRedirectUrlApi(targetUrl);

                                                                    if (response && response.redirectUrl) {
                                                                        window.open(response.redirectUrl, "_blank", "noopener,noreferrer");
                                                                    } else {
                                                                        console.error("Failed to get redirect URL");
                                                                    }
                                                                } catch (err) {
                                                                    console.error("Redirect error:", err);
                                                                }
                                                            }}
                                                        >
    <strong>Check Review</strong>
  </span>
                                                    </p>
                                                </Card.Body>
                                            </Card>
                                        </Col>
                                    </Row>
                                    </>)}
                                    {[10, 11, 14, 15, 22, 23].includes(book.selectedCurrentUserAssign?.assTypeId) && (<>
                                        {/* Две фото из БД */}
                                        {(photo1 || photo2) && (
                                            <>
                                                <h6 className="text-muted me-2">Your uploaded purchase confirmation files:</h6>
                                                <div
                                                    className="mt-2"
                                                    style={{
                                                        display: "grid",
                                                        gridTemplateColumns: proofUrls.length > 1 ? "repeat(2, minmax(0, 1fr))" : "minmax(0, 50%)",
                                                        justifyContent: "start",
                                                        gap: 12,
                                                        width: "100%",
                                                    }}
                                                >
                                                    {proofUrls.map((url, idx) => (
                                                        <ProofImageCard
                                                            key={`proof-${idx}`}
                                                            src={url}
                                                            alt={`Proof ${idx + 1}`}
                                                            fallbackSrc={proofFallback}
                                                        />
                                                    ))}
                                                </div>
                                            </>
                                        )}
                                    </>)}
                                    {[28, 29, 30, 31, 32].includes(book.selectedCurrentUserAssign?.assTypeId) && (<>
                                        {/* Две фото из БД */}
                                        {photo1 && (
                                            <>
                                                <h6 className="text-muted mt-3 me-2">Your uploaded confirmation file:</h6>
                                                <div
                                                    className="mt-2 mb-2"
                                                    style={{
                                                        display: "grid",
                                                        gridTemplateColumns: proofUrls.length > 1 ? "repeat(2, minmax(0, 1fr))" : "minmax(0, 50%)",
                                                        justifyContent: "start",
                                                        gap: 12,
                                                        width: "100%",
                                                    }}
                                                >
                                                    {proofUrls.map((url, idx) => (
                                                        <ProofImageCard
                                                            key={`proof-${idx}`}
                                                            src={url}
                                                            alt={`Proof ${idx + 1}`}
                                                            fallbackSrc={proofFallback}
                                                        />
                                                    ))}
                                                </div>
                                            </>
                                        )}
                                    </>)}

                                    {book?.selectedCurrentUserAssign?.checked_review_log && (
                                        <p className="mt-3 ms-2 py-1">
                                            <strong className="text-success text-muted">
                                                {errorMessage && '❌ ' + errorMessage}
                                                {book.selectedCurrentUserAssign?.checked_review_log}
                                            </strong>
                                        </p>
                                        )}

                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>



                </>
            )}

                {/* Блок 6:Summary */}
                <Row className="p-0 ps-2 pe-2 mt-0 mb-0 small">
                    <Col md={12} className="mb-0 mt-0 p-0 ">
                        {/* Верхний ряд с текстом */}
                        <Card className="h-100 rounded p-1 mb-0 border">
                            <Card.Body className="d-flex flex-column p-2 position-relative">
                    <div className="d-flex align-items-center flex-wrap">
                        <h5 className="mb-0 me-2 text-muted">Summary:</h5>
                        <b className="fs-5 text-muted">{book.selectedCurrentUserAssign?.book?.title || 'No title available'}</b>
                    </div>
                    <div className="mt-2 text-muted">
                        {book.selectedCurrentUserAssign?.book?.summary || 'No summary available'}
                    </div>
                            </Card.Body>
                        </Card>
                </Col>
            </Row>
                <Row className="p-0 ps-2 pe-2 mt-2 mb-0">
                    <Col md={12} className="mb-0 mt-0 p-0 ">
                <SimpleLogsViewerBA
                    userId={user.user.id}
                    type="assignment"   // или "book"
                    targetId={book.selectedCurrentUserAssign?.id}
                    width="659px"
                />
                    </Col>
                </Row>


            <CancelAssignment
                show={showCancelAssignment}
                assignobject={book?.selectedCurrentUserAssign}
                currentStep={currentStep}
                onClose={() => setShowCancelAssignment(false)}
                setBookAssignments={setBookAssignments}
                setActiveComponent={setActiveComponent}
                bonusToCancel = {book.selectedCurrentUserAssign?.received_bonus}
            />
            <RatingModal
                show={showRatingModal}
                onClose={() => setShowRatingModal(false)}
                assignobject={book?.selectedCurrentUserAssign}
                setCurrentStep={setCurrentStep}
            />
            <ChangeAmzName show={showChangeAmzName}
            onClose={() => setShowChangeAmzName(false)}
            assign={book?.selectedCurrentUserAssign}
            reviewData={reviewData}
            />

                </div>
            </Card>
        </>


                )}
        </>
    );
});

export default AssignFull;
